<template>
    <div>
        
        <!-- Sidebar -->
        <div class="task-sidebar" :class="{ 'is-open': isOpen }">
            <!-- Header -->
            <div class="task-sidebar-header">
                <div class="task-sidebar-actions">
                    <button v-if="subprojectId && taskProjectId" type="button" class="close-btn" @click="copyTaskLink" title="Copia link">
                        <i class="fa fa-link"></i>
                    </button>
                    <button v-if="subprojectId" type="button" class="close-btn" @click="toggleFollow" :title="isFollowing ? 'Non seguire più' : 'Segui'">
                        <i class="fa" :class="isFollowing ? 'fa-bell' : 'fa-bell-o'"></i>
                    </button>
                    <button type="button" class="close-btn" @click="close">
                        <i class="fa fa-times"></i>
                    </button>
                </div>
                <div class="task-sidebar-title">
                    <!-- Breadcrumb: Codice + Progetto > Task Padre -->
                    <div class="task-breadcrumb" v-if="!isCreationMode && (taskData.code || taskData.project_name || taskData.parent_name)">
                        <span v-if="taskData.code" class="breadcrumb-code">{{ taskData.code }}</span>
                        <span v-if="taskData.code && (taskData.project_name || taskData.parent_name)" class="breadcrumb-separator">·</span>
                        <span v-if="taskData.project_name" class="breadcrumb-item">{{ taskData.project_name }}</span>
                        <span v-if="taskData.project_name && taskData.parent_name" class="breadcrumb-separator">›</span>
                        <span v-if="taskData.parent_name" class="breadcrumb-item">{{ taskData.parent_name }}</span>
                    </div>
                    
                    <h4 
                        v-if="canModify && !isPlanningMode && subprojectId"
                        :key="'sidebar-title-' + subprojectId"
                        contenteditable="true"
                        @input="handleTitleInput"
                        @blur="saveTitleField"
                        @keydown.enter.prevent="$event.target.blur()"
                        class="editable-title"
                        ref="titleInput">{{ taskData.name }}</h4>
                    <h4 v-else :key="'sidebar-title-readonly-' + subprojectId" class="task-title-readonly">{{ taskData.name || 'Timesheet Progetto' }}</h4>
                    <div class="task-badges" v-if="subprojectId && !isPlanningMode">
                        <select
                            v-if="canEditTaskStatus && taskStatusSelectOptions.length"
                            class="form-control input-sm task-sidebar-status-select"
                            :value="taskData.status"
                            @change="saveTaskStatusFromSelect($event.target.value)"
                        >
                            <option
                                v-for="opt in taskStatusSelectOptions"
                                :key="opt.slug"
                                :value="opt.slug"
                            >
                                {{ opt.label }}
                            </option>
                        </select>
                        <span
                            v-else-if="taskData.status"
                            class="badge"
                            :style="statusBadgeStyle(taskData.status)"
                        >
                            {{ statusLabel(taskData.status) }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Tab Navigation -->
            <div class="task-sidebar-tabs">
                <button 
                    v-if="isPlanningMode && assignmentData"
                    class="tab-btn"
                    :class="{ active: activeTab === 'planning' }"
                    @click="activeTab = 'planning'">
                    <i class="fa fa-calendar-check-o"></i> Pianificazione
                </button>
                <button 
                    v-if="subprojectId"
                    class="tab-btn"
                    :class="{ active: activeTab === 'overview' }"
                    @click="activeTab = 'overview'">
                    <i class="fa fa-info-circle"></i> Panoramica
                </button>
                <button 
                    v-if="subprojectId"
                    class="tab-btn"
                    :class="{ active: activeTab === 'attachments' }"
                    @click="activeTab = 'attachments'">
                    <i class="fa fa-paperclip"></i> Allegati ({{ attachments.length }})
                </button>
                <button 
                    v-if="!isCreationMode"
                    class="tab-btn"
                    :class="{ active: activeTab === 'timesheet' }"
                    @click="loadTimesheetData">
                    <i class="fa fa-clock-o"></i> Timesheet ({{ timesheetEntries.length }})
                </button>
            </div>

            <!-- Tab Content -->
            <div class="task-sidebar-content" ref="sidebarContent" :class="{ 'content-loading': isLoadingTask }">
                <!-- Overlay Loading durante cambio task -->
                <div v-if="isLoadingTask" class="task-loading-overlay">
                    <div class="loading-spinner">
                        <i class="fa fa-spinner fa-spin fa-2x"></i>
                        <span>Caricamento task...</span>
                    </div>
                </div>
                
                <!-- Tab Pianificazione (solo Planning Mode) -->
                <div v-if="isPlanningMode && assignmentData" v-show="activeTab === 'planning'" class="tab-content planning-tab">
                    <!-- Blocco Assegnazione Settimana -->
                    <!-- Blocco statistiche ore (solo in modalità modifica) -->
                    <div v-if="!isCreationMode" class="assignment-week-block">
                        <div class="assignment-header">
                            <div class="assignment-user">
                                <img v-if="assignmentData.user.avatar" :src="assignmentData.user.avatar" :alt="assignmentData.user.full_name" class="user-avatar-medium">
                                <div v-else class="user-avatar-placeholder-medium">
                                    {{ assignmentData.user.initials }}
                                </div>
                                <div class="user-info">
                                    <strong>{{ assignmentData.user.full_name }}</strong>
                                    <span class="user-role">{{ assignmentData.user.role }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="assignment-stats">
                            <div class="stat-item planned">
                                <i class="fa fa-calendar"></i>
                                <div class="stat-content">
                                    <span class="stat-label">Pianificate</span>
                                    <span class="stat-value">{{ assignmentData.planned_hours }}h</span>
                                </div>
                            </div>
                            <div class="stat-item actual">
                                <i class="fa fa-check-circle"></i>
                                <div class="stat-content">
                                    <span class="stat-label">Effettive</span>
                                    <span class="stat-value">{{ assignmentData.actual_hours }}h</span>
                                </div>
                            </div>
                            <div class="stat-item difference" :class="{ 'positive': assignmentData.actual_hours >= assignmentData.planned_hours, 'negative': assignmentData.actual_hours < assignmentData.planned_hours }">
                                <i class="fa" :class="assignmentData.actual_hours >= assignmentData.planned_hours ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
                                <div class="stat-content">
                                    <span class="stat-label">Differenza</span>
                                    <span class="stat-value">{{ Math.abs(assignmentData.actual_hours - assignmentData.planned_hours) }}h</span>
                                </div>
                            </div>
                        </div>
                        <div class="progress-bar-container">
                            <div class="progress-bar-fill" :style="{ width: Math.min((assignmentData.actual_hours / assignmentData.planned_hours) * 100, 100) + '%' }"></div>
                        </div>
                        <div class="progress-percentage">
                            {{ Math.round((assignmentData.actual_hours / assignmentData.planned_hours) * 100) }}% completato
                        </div>
                    </div>


                    <!-- Dettaglio descrizione (visibile anche senza permesso di modifica, e anche con ore consuntivate) -->
                    <div v-if="assignmentData.actual_hours > 0 && assignmentData.description" class="planning-description-readonly">
                        <h6 style="margin-bottom: 10px; color: #aaa; font-size: 13px;">
                            <i class="fa fa-align-left"></i> Descrizione pianificazione
                        </h6>
                        <div class="description-readonly-content" v-html="assignmentData.description" style="background: #2a2a2a; border: 1px solid #444; border-radius: 4px; padding: 12px; color: #e0e0e0; font-size: 13px; line-height: 1.6; max-height: 300px; overflow-y: auto;"></div>
                    </div>

                    <!-- Modifica Assegnazione -->
                    <div v-if="canModify" class="edit-assignment-section">
                        <div v-if="assignmentData.actual_hours > 0" class="alert alert-warning" style="margin: 0;">
                            <i class="fa fa-exclamation-triangle"></i>
                            Non puoi modificare progetto/task per assegnazioni già iniziate ({{ assignmentData.actual_hours }}h consuntivate)
                        </div>
                        <template v-else>
                            <h6>
                                <i class="fa" :class="isCreationMode ? 'fa-plus' : 'fa-exchange'"></i>
                                {{ isCreationMode ? 'Nuova Assegnazione' : 'Modifica Assegnazione' }}
                            </h6>

                            <!-- Campo Progetto con Autocomplete -->
                            <div class="autocomplete-container">
                                <label>Progetto</label>
                                <div style="position: relative;">
                                    <input 
                                        type="text"
                                        v-model="projectSearchQuery"
                                        @input="filterProjects"
                                        @focus="showProjectDropdown = true"
                                        @blur="hideProjectDropdownDelayed"
                                        @keydown="handleProjectKeydown"
                                        class="autocomplete-input"
                                        placeholder="Cerca progetto..."
                                    />
                                    <i 
                                        v-if="editingAssignment.newProjectName"
                                        class="fa fa-times autocomplete-clear"
                                        @click="clearProject"
                                    ></i>
                                </div>
                                <div 
                                    v-if="showProjectDropdown && projectSearchQuery.length >= 2" 
                                    class="autocomplete-dropdown"
                                    @mousedown.prevent
                                >
                                    <div 
                                        v-if="isLoadingProjects" 
                                        class="autocomplete-no-results"
                                    >
                                        <i class="fa fa-spinner fa-spin"></i> Caricamento...
                                    </div>
                                    <div 
                                        v-else-if="filteredProjects.length === 0" 
                                        class="autocomplete-no-results"
                                    >
                                        Nessun progetto trovato
                                    </div>
                                    <div 
                                        v-else
                                        v-for="(project, index) in filteredProjects"
                                        :key="project.id"
                                        :class="['autocomplete-item', `autocomplete-item-project-${index}`, { 'autocomplete-item-selected': index === selectedProjectIndex }]"
                                        @click="selectProject(project)"
                                    >
                                        {{ project.title }} <span style="color: #888;">({{ project.client_name }})</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Campo Task con Autocomplete Gerarchico -->
                            <div class="autocomplete-container">
                                <label>Task (opzionale)</label>
                                <div style="position: relative;">
                                    <input 
                                        type="text"
                                        v-model="subprojectSearchQuery"
                                        @input="filterSubprojects"
                                        @focus="onSubprojectInputFocus"
                                        @blur="handleMainSubprojectInputBlur"
                                        @keydown="handleSubprojectKeydown"
                                        class="autocomplete-input"
                                        placeholder="Cerca task..."
                                        :disabled="!editingAssignment.newProjectId"
                                    />
                                    <i 
                                        v-if="editingAssignment.newSubprojectName"
                                        class="fa fa-times autocomplete-clear"
                                        @click="clearSubproject"
                                    ></i>
                                </div>
                                <div 
                                    v-if="showSubprojectDropdown && editingAssignment.newProjectId" 
                                    class="autocomplete-dropdown"
                                    @mousedown.prevent
                                >
                                    <div 
                                        v-if="isLoadingSubprojects" 
                                        class="autocomplete-no-results"
                                    >
                                        <i class="fa fa-spinner fa-spin"></i> Caricamento...
                                    </div>
                                    <div 
                                        v-else-if="filteredSubprojects.length === 0 && !subprojectSearchQuery" 
                                        class="autocomplete-no-results"
                                    >
                                        Nessun task trovato
                                    </div>
                                    <template v-else>
                                        <!-- Lista task esistenti con input inline per sottotask -->
                                        <template v-for="(subproject, index) in filteredSubprojects">
                                            <div 
                                                :key="subproject.id"
                                                :class="['autocomplete-item', `autocomplete-item-subproject-${index}`, { 'autocomplete-item-selected': index === selectedSubprojectIndex }]"
                                                :style="{ paddingLeft: (8 + (subproject.indent_level || 0) * 20) + 'px', display: 'flex', alignItems: 'center' }"
                                                @mouseenter="selectedSubprojectIndex = index"
                                                @mouseleave="selectedSubprojectIndex = -1"
                                            >
                                                <span @click="selectSubproject(subproject)" style="flex: 1; cursor: pointer;">
                                                    {{ subproject.name }}
                                                </span>
                                                <!-- Pulsante + per creare sottotask -->
                                                <button
                                                    v-if="canModify && creatingSubtaskForParentId !== subproject.id"
                                                    @click.stop="startCreatingSubtask(subproject.id)"
                                                    class="subtask-create-btn"
                                                    :title="'Crea sottotask di ' + subproject.name"
                                                    style="margin-left: auto; padding: 2px 6px; background: #444; border: none; color: #fff; border-radius: 3px; cursor: pointer; font-size: 12px; opacity: 0.7; transition: opacity 0.2s;"
                                                    @mouseenter="$event.target.style.opacity = '1'"
                                                    @mouseleave="$event.target.style.opacity = '0.7'"
                                                >
                                                    <i class="fa fa-plus"></i>
                                                </button>
                                            </div>
                                            
                                            <!-- Input inline per creare sottotask (appare subito dopo il task padre) -->
                                            <div 
                                                v-if="creatingSubtaskForParentId === subproject.id"
                                                :key="'subtask-input-' + subproject.id"
                                                class="subtask-create-input-container"
                                                :style="{ padding: '6px', paddingLeft: (8 + ((subproject.indent_level || 0) + 1) * 20) + 'px' }"
                                                @mousedown.prevent
                                            >
                                                <input
                                                    v-model="newSubtaskName"
                                                    @keydown.enter.prevent="createQuickSubtask(subproject.id)"
                                                    @keydown.escape="cancelCreatingSubtask"
                                                    @mousedown.stop.prevent
                                                    @click.stop.prevent
                                                    @focus.stop.prevent
                                                    @blur="handleSubtaskInputBlur"
                                                    @focusin.stop
                                                    class="subtask-create-input"
                                                    placeholder="Nome sottotask..."
                                                    style="width: 100%; padding: 6px 8px; background: #333; border: 1px solid #555; color: #fff; border-radius: 3px; font-size: 13px;"
                                                    ref="subtaskInputRef"
                                                />
                                            </div>
                                        </template>
                                        
                                        <!-- Opzione "Crea nuovo task" se c'è una ricerca -->
                                        <div 
                                            v-if="subprojectSearchQuery && subprojectSearchQuery.length > 0 && filteredSubprojects.length === 0"
                                            class="autocomplete-item autocomplete-item-create"
                                            :class="{ 'autocomplete-item-selected': selectedSubprojectIndex === -1 && filteredSubprojects.length === 0 }"
                                            @click="createQuickTask()"
                                            style="border-top: 1px solid #444; padding: 10px 12px; color: #4CAF50; font-weight: 500; cursor: pointer;"
                                        >
                                            <i class="fa fa-plus-circle"></i> Crea nuovo task: "{{ subprojectSearchQuery }}"
                                        </div>
                                        
                                        <!-- Opzione "Crea nuovo task" anche se ci sono risultati ma l'utente vuole creare uno nuovo -->
                                        <div 
                                            v-if="subprojectSearchQuery && subprojectSearchQuery.length > 0 && filteredSubprojects.length > 0 && !isExactMatch(subprojectSearchQuery)"
                                            class="autocomplete-item autocomplete-item-create"
                                            :class="{ 'autocomplete-item-selected': selectedSubprojectIndex === filteredSubprojects.length }"
                                            @click="createQuickTask()"
                                            style="border-top: 1px solid #444; padding: 10px 12px; color: #4CAF50; font-weight: 500; cursor: pointer;"
                                        >
                                            <i class="fa fa-plus-circle"></i> Crea nuovo task: "{{ subprojectSearchQuery }}"
                                        </div>
                                    </template>
                                </div>
                            </div>

                            <!-- Campo Utente con Autocomplete -->
                            <div class="autocomplete-container">
                                <label>Assegna a Utente</label>
                                <div style="position: relative;">
                                    <input 
                                        type="text"
                                        v-model="userSearchQuery"
                                        @input="filterUsers"
                                        @focus="onUserInputFocus"
                                        @blur="hideUserDropdownDelayed"
                                        @keydown="handleUserKeydown"
                                        class="autocomplete-input"
                                        placeholder="Cerca utente..."
                                    />
                                    <i 
                                        v-if="editingAssignment.newUserName"
                                        class="fa fa-times autocomplete-clear"
                                        @click="clearUser"
                                    ></i>
                                </div>
                                <div 
                                    v-if="showUserDropdown" 
                                    class="autocomplete-dropdown"
                                    @mousedown.prevent
                                >
                                    <div 
                                        v-if="filteredUsers.length === 0" 
                                        class="autocomplete-no-results"
                                    >
                                        Nessun utente trovato
                                    </div>
                                    <div 
                                        v-else
                                        v-for="(user, index) in filteredUsers"
                                        :key="user.id"
                                        :class="['autocomplete-item', `autocomplete-item-user-${index}`, { 'autocomplete-item-selected': index === selectedUserIndex }]"
                                        @click="selectUser(user)"
                                    >
                                        {{ user.full_name }} <span v-if="user.division" style="color: #888;">({{ user.division }})</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Campo Ore Pianificate -->
                            <div class="form-group" style="margin-bottom: 15px;">
                                <label>Nuove Ore Pianificate *</label>
                                <input 
                                    type="number"
                                    v-model.number="editingAssignment.newHours"
                                    class="form-control"
                                    min="0.5"
                                    step="0.5"
                                    placeholder="Inserisci ore..."
                                    style="background: #2a2a2a; border: 1px solid #444; color: #e0e0e0;"
                                />
                            </div>

                            <!-- Campo Descrizione -->
                            <div class="form-group" style="margin-bottom: 15px;">
                                <label>Descrizione</label>
                                <textarea 
                                    v-model="editingAssignment.newDescription"
                                    class="form-control"
                                    rows="4"
                                    placeholder="Note o descrizione dell'assegnazione (opzionale)..."
                                    style="background: #2a2a2a; border: 1px solid #444; color: #e0e0e0;"
                                ></textarea>
                            </div>

                            <!-- Data specifica (opzionale): sempre in pianificazione, non solo con flag "daily planning" in impostazioni -->
                            <div class="form-group" style="margin-bottom: 15px;">
                                <label>Data specifica (opzionale)</label>
                                <input 
                                    ref="taskSidebarSpecificDateInput"
                                    type="text"
                                    class="form-control date-picker"
                                    :data-field="'task_sidebar_specific_date_hidden'"
                                    placeholder="Seleziona data"
                                    style="background: #2a2a2a; border: 1px solid #444; color: #e0e0e0;"
                                />
                                <input 
                                    type="hidden" 
                                    id="task_sidebar_specific_date_hidden"
                                    :value="editingAssignment.specificDate"
                                />
                                <small style="display: block; margin-top: 5px; color: #888; font-size: 12px;">
                                    <template v-if="isCreationMode">
                                        Lascia vuoto per associare il task all'intera settimana, oppure seleziona un giorno nella settimana in cui stai creando l'assegnazione.
                                    </template>
                                    <template v-else>
                                        Lascia vuoto per associare il task all'intera settimana, oppure scegli un giorno specifico (anche in una settimana successiva).
                                    </template>
                                </small>
                            </div>

                            <!-- Pulsante Applica/Crea -->
                            <button 
                                type="button"
                                class="btn btn-warning btn-sm"
                                @click="confirmAssignmentChange"
                                :disabled="isSavingAssignment || !isAssignmentValid"
                                style="width: 100%;"
                            >
                                <i class="fa" :class="isSavingAssignment ? 'fa-spinner fa-spin' : (isCreationMode ? 'fa-plus' : 'fa-check')"></i>
                                {{ isSavingAssignment ? 'Salvataggio...' : (isCreationMode ? 'Crea Assegnazione' : 'Applica Modifica') }}
                            </button>
                        </template>
                    </div>
                </div>

                <!-- Tab Panoramica (Descrizione + Chat unificati) -->
                <div v-if="subprojectId" v-show="activeTab === 'overview'" class="tab-content overview-tab">
                    <!-- Blocco KPI Task -->
                    <div v-if="taskData.expected_duration || taskData.planned_hours || taskData.total_hours" class="task-kpi-block">
                        <div class="kpi-stats">
                            <!-- Preventivate (nascosto se 0) -->
                            <div v-if="taskData.expected_duration && taskData.expected_duration > 0" class="kpi-item estimated">
                                <i class="fa fa-file-text-o"></i>
                                <div class="kpi-content">
                                    <span class="kpi-label">Preventivate</span>
                                    <span class="kpi-value">{{ Math.round(taskData.expected_duration) }}h</span>
                                </div>
                            </div>
                            <!-- Pianificate -->
                            <div class="kpi-item planned">
                                <i class="fa fa-calendar"></i>
                                <div class="kpi-content">
                                    <span class="kpi-label">Pianificate</span>
                                    <span class="kpi-value">{{ Math.round(taskData.planned_hours || 0) }}h</span>
                                </div>
                            </div>
                            <!-- Effettive -->
                            <div class="kpi-item actual">
                                <i class="fa fa-check-circle"></i>
                                <div class="kpi-content">
                                    <span class="kpi-label">Effettive</span>
                                    <span class="kpi-value">{{ Math.round(taskData.total_hours || 0) }}h</span>
                                </div>
                            </div>
                            <!-- Differenza (solo se preventivate > 0) -->
                            <div v-if="taskData.expected_duration && taskData.expected_duration > 0" class="kpi-item difference" 
                                 :class="{
                                     'positive': (taskData.expected_duration - ((taskData.planned_hours || 0) + (taskData.total_hours || 0))) >= 0,
                                     'negative': (taskData.expected_duration - ((taskData.planned_hours || 0) + (taskData.total_hours || 0))) < 0
                                 }">
                                <i class="fa" :class="(taskData.expected_duration - ((taskData.planned_hours || 0) + (taskData.total_hours || 0))) >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
                                <div class="kpi-content">
                                    <span class="kpi-label">Differenza</span>
                                    <span class="kpi-value">{{ Math.round(Math.abs(taskData.expected_duration - ((taskData.planned_hours || 0) + (taskData.total_hours || 0)))) }}h</span>
                                </div>
                            </div>
                        </div>
                        <!-- Barra progresso (senza testo percentuale) -->
                        <div v-if="taskData.expected_duration && taskData.expected_duration > 0" class="kpi-progress-container">
                            <div class="kpi-progress-bar-fill" :style="{ 
                                width: Math.min(((taskData.total_hours || 0) / taskData.expected_duration) * 100, 100) + '%' 
                            }"></div>
                        </div>
                    </div>

                    <!-- Task Info Compatta - Una riga -->
                    <div class="task-info-row">
                        <!-- Ore previste -->
                        <div class="info-item-inline" v-if="(canModify && !isPlanningMode && !taskData.has_children) || taskData.expected_duration">
                            <i class="fa fa-hourglass-half"></i>
                            <span 
                                v-if="canModify && !isPlanningMode && !taskData.has_children"
                                contenteditable="true"
                                @input="handleFieldInput('expected_duration', $event)"
                                @blur="saveField('expected_duration')"
                                @keydown.enter.prevent="$event.target.blur()"
                                class="info-value editable"
                                :data-placeholder="!taskData.expected_duration ? '-' : ''">{{ taskData.expected_duration || '' }}</span>
                            <span v-else class="info-value">{{ taskData.expected_duration }}h</span>
                        </div>
                        
                        <!-- Data Inizio -->
                        <div class="info-item-inline" v-if="(canModify && !isPlanningMode && !taskData.has_children) || taskData.start_date">
                            <i class="fa fa-calendar"></i>
                            <template v-if="canModify && !isPlanningMode && !taskData.has_children">
                                <input 
                                    v-if="isEditingStartDate || editingFields.start_date"
                                    type="date"
                                    v-model="editingFields.start_date"
                                    @change="saveStartDate"
                                    @blur="isEditingStartDate = false"
                                    ref="startDateInput"
                                    class="inline-date-input" />
                                <span 
                                    v-else 
                                    class="info-value editable-placeholder" 
                                    @click="startEditingStartDate"
                                    title="Data inizio">
                                    {{ taskData.start_date || 'Inizio' }}
                                </span>
                            </template>
                            <span v-else class="info-value">{{ taskData.start_date }}</span>
                        </div>

                        <!-- Data Fine -->
                        <div class="info-item-inline" v-if="(canModify && !isPlanningMode && !taskData.has_children) || taskData.end_date">
                            <i class="fa fa-calendar-check-o"></i>
                            <template v-if="canModify && !isPlanningMode && !taskData.has_children">
                                <input 
                                    v-if="isEditingEndDate || editingFields.end_date"
                                    type="date"
                                    v-model="editingFields.end_date"
                                    @change="saveEndDate"
                                    @blur="isEditingEndDate = false"
                                    ref="endDateInput"
                                    class="inline-date-input" />
                                <span 
                                    v-else 
                                    class="info-value editable-placeholder" 
                                    @click="startEditingEndDate"
                                    title="Data fine"
                                    :class="{ 'text-muted': !taskData.end_date && taskData.suggested_end_date }">
                                    {{ taskData.end_date || (taskData.suggested_end_date ? '~' + taskData.suggested_end_date : 'Fine') }}
                                </span>
                            </template>
                            <span v-else class="info-value">{{ taskData.end_date }}</span>
                        </div>

                        <!-- Scadenza -->
                        <div class="info-item-inline" v-if="(canModify && !isPlanningMode) || taskData.due_date">
                            <i class="fa fa-clock-o"></i>
                            <template v-if="canModify && !isPlanningMode">
                                <input 
                                    v-if="isEditingDueDate || editingFields.due_date"
                                    type="date"
                                    v-model="editingFields.due_date"
                                    @change="saveDueDate"
                                    @blur="isEditingDueDate = false"
                                    ref="dueDateInput"
                                    class="inline-date-input" />
                                <span 
                                    v-else 
                                    class="info-value editable-placeholder" 
                                    @click="startEditingDueDate">
                                    {{ taskData.due_date || '--/--' }}
                                </span>
                            </template>
                            <span v-else class="info-value">{{ taskData.due_date || '--/--' }}</span>
                        </div>
                        
                        <!-- Tipo Task -->
                        <div class="info-item-inline" v-if="(canModify && !isPlanningMode) || taskData.commercial_agreement">
                            <i class="fa fa-tag"></i>
                            <span 
                                v-if="canModify && !isPlanningMode"
                                class="info-value editable clickable"
                                @click.stop="openTypeSelector">
                                {{ editingFields.commercial_agreement || taskData.commercial_agreement || '-' }}
                                <i class="fa fa-caret-down" style="font-size: 10px;"></i>
                            </span>
                            <span v-else class="info-value">{{ taskData.commercial_agreement }}</span>
                        </div>
                        
                        <!-- Priorità -->
                        <div class="info-item-inline" v-if="(canModify && !isPlanningMode) || taskData.priority">
                            <i class="fa fa-flag"></i>
                            <select 
                                v-if="canModify && !isPlanningMode"
                                v-model="editingFields.priority"
                                @change="savePriority"
                                class="inline-priority-select"
                                :class="'priority-' + (editingFields.priority || taskData.priority)">
                                <option value="low">Bassa</option>
                                <option value="medium">Media</option>
                                <option value="high">Alta</option>
                            </select>
                            <span v-else class="info-value" :class="'priority-text-' + taskData.priority">
                                {{ priorityLabel(taskData.priority) }}
                            </span>
                        </div>

                        <!-- Foglio (solo task principali) -->
                        <div class="info-item-inline" v-if="taskData.show_task_sheet_field && ((canModify && !isPlanningMode) || taskData.task_sheet_id)">
                            <i class="fa fa-folder-open-o"></i>
                            <select
                                v-if="canModify && !isPlanningMode"
                                v-model="editingFields.task_sheet_id"
                                @change="saveTaskSheet"
                                class="inline-priority-select">
                                <option :value="null">Nessun foglio</option>
                                <option v-for="sheet in taskData.task_sheets" :key="sheet.id" :value="sheet.id">{{ sheet.name }}</option>
                            </select>
                            <span v-else class="info-value">{{ taskSheetLabel(taskData.task_sheet_id) }}</span>
                        </div>
                        
                        <!-- Separatore verticale -->
                        <div v-if="(canModify && !isPlanningMode) || (taskData.assigned_users && taskData.assigned_users.length > 0)" class="info-separator"></div>
                        
                        <!-- Utenti Assegnati - Inline (ora dentro task-info-row) -->
                        <div v-if="(canModify && !isPlanningMode) || (taskData.assigned_users && taskData.assigned_users.length > 0)" class="assigned-users-inline-compact">
                            <span class="assigned-label" :class="{ clickable: canModify && !isPlanningMode }" @click="(canModify && !isPlanningMode) && openAssigneeSelector()">
                                <i class="fa fa-users"></i>
                            </span>
                            <div class="users-avatars-inline">
                                <div v-for="user in taskData.assigned_users" :key="user.id" class="user-avatar-wrapper">
                                    <div class="user-avatar-small" :title="user.full_name">
                                        <img v-if="user.avatar" :src="user.avatar" :alt="user.full_name">
                                        <span v-else class="initials">{{ user.initials }}</span>
                                    </div>
                                    <button 
                                        v-if="canModify && !isPlanningMode" 
                                        class="user-remove-btn" 
                                        @click.stop="removeAssignee(user.id)" 
                                        title="Rimuovi">&times;</button>
                                </div>
                                <div v-if="canModify && !isPlanningMode" class="user-avatar-small add-btn" @click="openAssigneeSelector" title="Aggiungi">
                                    <i class="fa fa-plus"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Dipendenze -->
                    <div v-if="canModify || (taskData.predecessors && taskData.predecessors.length) || (taskData.successors && taskData.successors.length)" class="dependencies-section">
                        <h5 class="section-title" style="margin-bottom: 8px;">
                            <i class="fa fa-link"></i> Dipendenze
                        </h5>

                        <div v-if="taskData.predecessors && taskData.predecessors.length" class="dep-list">
                            <div v-for="pred in taskData.predecessors" :key="'pred-' + pred.id" class="dep-item">
                                <i class="fa fa-arrow-left dep-icon pred"></i>
                                <span class="dep-code" v-if="pred.code">{{ pred.code }}</span>
                                <span class="dep-name">{{ pred.name }}</span>
                                <button v-if="canModify && !isPlanningMode" class="dep-remove" @click.stop="removeDependency(pred.pivot_id)" title="Rimuovi dipendenza">
                                    <i class="fa fa-times"></i>
                                </button>
                            </div>
                        </div>

                        <div v-if="taskData.successors && taskData.successors.length" class="dep-list">
                            <div v-for="succ in taskData.successors" :key="'succ-' + succ.id" class="dep-item">
                                <i class="fa fa-arrow-right dep-icon succ"></i>
                                <span class="dep-code" v-if="succ.code">{{ succ.code }}</span>
                                <span class="dep-name">{{ succ.name }}</span>
                                <button v-if="canModify && !isPlanningMode" class="dep-remove" @click.stop="removeDependency(succ.pivot_id)" title="Rimuovi dipendenza">
                                    <i class="fa fa-times"></i>
                                </button>
                            </div>
                        </div>

                        <div v-if="canModify && !isPlanningMode" class="dep-add">
                            <div v-if="!isAddingDep" class="dep-add-trigger" @click="isAddingDep = true">
                                <i class="fa fa-plus"></i> Aggiungi predecessore
                            </div>
                            <div v-else class="dep-add-form">
                                <input
                                    ref="depSearchInput"
                                    type="text"
                                    v-model="depSearchQuery"
                                    @input="searchDependencyTasks"
                                    @blur="closeDepSearch"
                                    @keydown.escape="isAddingDep = false"
                                    placeholder="Cerca task..."
                                    class="dep-search-input" />
                                <div v-if="depSearchResults.length" class="dep-search-results">
                                    <div v-for="result in depSearchResults" :key="result.id"
                                         class="dep-search-item"
                                         @mousedown.prevent="addDependency(result.id)">
                                        <span class="dep-code" v-if="result.code">{{ result.code }}</span>
                                        {{ result.name }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="!taskData.predecessors.length && !taskData.successors.length && !(canModify && !isPlanningMode)" class="dep-empty">
                            Nessuna dipendenza
                        </div>
                    </div>

                    <!-- Creato da -->
                    <div v-if="taskData.creator" class="task-created-info">
                        <i class="fa fa-user-o"></i> Creato da <strong>{{ taskData.creator.full_name }}</strong> il {{ taskData.created_at }}
                    </div>

                    <!-- Descrizione -->
                    <div class="description-section"
                         @dragover.prevent="handleDescriptionAreaDragover"
                         @dragleave.prevent="handleDescriptionAreaDragleave"
                         @drop.prevent="handleDescriptionDrop">
                        <h5 class="section-title">
                            <i class="fa fa-align-left"></i> Descrizione
                        </h5>
                        <!-- Vista renderizzata (non in editing) -->
                        <div v-if="!isEditingDescription" 
                             class="description-view" 
                             :class="{ 'is-empty': !descriptionText, 'is-dragover': descriptionDragover }"
                             @click="handleDescriptionViewClick"
                             v-html="descriptionHtml || ''">
                        </div>
                        <!-- Editor WYSIWYG (solo in editing) -->
                        <template v-else>
                            <wysiwyg-editor
                                ref="descriptionEditor"
                                v-model="descriptionText"
                                :users="projectUsers"
                                :attachments="attachments"
                                :disabled="!canModify"
                                :task-id="subprojectId"
                                mode="description"
                                @blur="handleDescriptionBlurWysiwyg"
                                @image-uploaded="handleDescriptionImageUploaded"
                                @file-uploaded="handleDescriptionFileUploaded"
                                @file-pasted="handleDescriptionFilePasted"
                                @preview-image="openImagePreview"
                                @upload-error="showError"
                                placeholder="Aggiungi una descrizione..."
                            ></wysiwyg-editor>
                        </template>
                        <div v-if="isSavingDescription" class="description-saving-indicator">
                            <i class="fa fa-spinner fa-spin"></i> Salvataggio...
                        </div>

                        <!-- Galleria: solo allegati non già inseriti nel testo con [img:ID] / [video:ID] / [file:ID] -->
                        <div v-if="descriptionGalleryAttachments.length > 0" class="description-attachments-gallery">
                            <div v-for="attachment in descriptionGalleryAttachments" :key="attachment.id" class="gallery-item"
                                 @click="handleGalleryItemClick(attachment)">
                                <div v-if="isImageAttachment(attachment)" class="gallery-thumb">
                                    <img :src="attachment.download_url" :alt="attachment.file_name" loading="lazy">
                                </div>
                                <div v-else class="gallery-file-icon">
                                    <i class="fa" :class="attachment.icon"></i>
                                </div>
                                <div class="gallery-item-name">{{ attachment.file_name }}</div>
                                <button v-if="canModify" type="button" class="gallery-item-delete"
                                        @click.stop="deleteAttachment(attachment.id)"
                                        title="Elimina">
                                    <i class="fa fa-times"></i>
                                </button>
                            </div>
                        </div>

                        <!-- Drop overlay -->
                        <div v-if="descriptionDragover" class="description-drop-overlay">
                            <i class="fa fa-cloud-upload"></i>
                            <span>Rilascia per allegare</span>
                        </div>
                    </div>

                    <!-- Divisore -->
                  

                    <!-- Chat/Commenti -->
                    <div class="chat-section">
                        <h5 class="section-title">
                            <i class="fa fa-comments"></i> Discussione ({{ comments.length }})
                        </h5>
                        <div class="activity-filter">
                            <button type="button" class="btn btn-xs" :class="activityFilter === 'comments' ? 'btn-primary' : 'btn-default'" @click="activityFilter = 'comments'">Commenti</button>
                            <button type="button" class="btn btn-xs" :class="activityFilter === 'all' ? 'btn-primary' : 'btn-default'" @click="activityFilter = 'all'">Tutta l'attività</button>
                        </div>
                        
                        <div class="comments-list" ref="commentsList">
                            <div v-if="discussionEntries.length === 0" class="empty-state-small">
                                <i class="fa fa-comments-o"></i>
                                <p>Inizia la discussione</p>
                            </div>
                            <div v-for="entry in discussionEntries" :key="entry.key">
                            <div v-if="entry.kind === 'activity'" class="activity-item">
                                <i class="fa fa-history"></i>
                                <span><strong>{{ entry.activity.user_name }}</strong> {{ entry.activity.summary }}</span>
                                <span class="comment-time">{{ entry.activity.created_at }}</span>
                            </div>
                            <div v-else
                                 class="comment-item"
                                 :class="{ 'is-own': entry.comment.is_own, 'is-editing': editingCommentId === entry.comment.id }">
                                <div class="comment-avatar">
                                    <img v-if="entry.comment.user.avatar" :src="entry.comment.user.avatar" :alt="entry.comment.user.full_name">
                                    <div v-else class="avatar-placeholder">
                                        {{ entry.comment.user.full_name.charAt(0) }}
                                    </div>
                                </div>
                                <div class="comment-content">
                                    <div class="comment-header">
                                        <strong>{{ entry.comment.user.full_name }}</strong>
                                        <span class="comment-time">{{ entry.comment.created_at }}</span>
                                        <span v-if="entry.comment.updated_at && entry.comment.updated_at !== entry.comment.created_at" class="comment-edited">(modificato)</span>
                                    </div>
                                    <!-- Modalità visualizzazione -->
                                    <div v-if="editingCommentId !== entry.comment.id" class="comment-text" v-html="entry.comment.formatted_comment" @click="handleCommentImageClick($event)"></div>
                                    <!-- Modalità modifica -->
                                    <div v-else class="comment-edit-form">
                                        <wysiwyg-editor
                                            :key="'edit-comment-' + editingCommentId"
                                            ref="editCommentEditor"
                                            v-model="editingCommentText"
                                            :users="projectUsers"
                                            :attachments="attachments"
                                            :task-id="subprojectId"
                                            :initial-mentioned-user-ids="editingCommentBaselineMentions"
                                            mode="comment"
                                            @mentioned="handleEditMention"
                                            @file-uploaded="handleDescriptionFileUploaded"
                                            @image-uploaded="handleDescriptionFileUploaded"
                                            @submit="saveEditComment(entry.comment.id)"
                                            @preview-image="openImagePreview"
                                            @upload-error="showError"
                                            placeholder="Modifica commento..."
                                        ></wysiwyg-editor>
                                        <div class="comment-edit-actions">
                                            <span v-if="isSavingComment" class="comment-autosave-indicator">
                                                <i class="fa fa-spinner fa-spin"></i> Salvataggio...
                                            </span>
                                            <button type="button" class="btn btn-sm btn-secondary" @click="cancelEditComment">
                                                Annulla
                                            </button>
                                            <button type="button" class="btn btn-sm btn-primary" @click="saveEditComment(entry.comment.id)" :disabled="isEditCommentEmpty || isSavingComment">
                                                <i v-if="isSavingComment" class="fa fa-spinner fa-spin"></i>
                                                <span v-else>Salva</span>
                                            </button>
                                        </div>
                                    </div>
                                    <!-- Barra reazioni + azioni -->
                                    <div v-if="editingCommentId !== entry.comment.id" class="comment-footer">
                                        <div class="comment-reactions">
                                            <button
                                                type="button"
                                                class="btn-reaction"
                                                :class="{ 'is-reacted': entry.comment.has_reacted }"
                                                @click="toggleReaction(entry.comment)"
                                                :disabled="entry.comment._reactingInProgress"
                                                :title="reactionTooltip(entry.comment)">
                                                <i class="fa fa-thumbs-up"></i>
                                                <span v-if="entry.comment.reactions_count > 0" class="reaction-count">{{ entry.comment.reactions_count }}</span>
                                            </button>
                                        </div>
                                        <div v-if="entry.comment.is_own" class="comment-actions">
                                            <button
                                                type="button"
                                                class="btn-edit-comment"
                                                @click="startEditComment(entry.comment)">
                                                <i class="fa fa-pencil"></i>
                                            </button>
                                            <button
                                                type="button"
                                                class="btn-delete-comment"
                                                @click="deleteComment(entry.comment.id)">
                                                <i class="fa fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>
                        </div>
                    </div>
                    
                </div>

                <!-- Tab Timesheet -->
                <div v-show="activeTab === 'timesheet'" class="tab-content">
                    <div class="timesheet-section">
                        <!-- Intestazione settimana -->
                        <div v-if="weekStart && weekEnd" class="week-indicator">
                            <i class="fa fa-calendar-check-o"></i>
                            Settimana: {{ formatWeekDisplay(weekStart) }} - {{ formatWeekDisplay(weekEnd) }}
                        </div>
                        
                        <div v-if="isLoadingTimesheet" class="loading-indicator">
                            <i class="fa fa-spinner fa-spin"></i> Caricamento timesheet...
                        </div>
                        
                        <div v-else-if="timesheetEntries.length === 0" class="empty-state">
                            <i class="fa fa-clock-o"></i>
                            <p v-if="weekStart && weekEnd">Nessun timesheet registrato per questa settimana</p>
                            <p v-else>Nessun timesheet registrato</p>
                        </div>
                        
                        <div v-else class="timesheet-list">
                            <div v-for="entry in timesheetEntries" :key="entry.id" class="timesheet-item">
                                <div class="timesheet-header-row">
                                    <div class="timesheet-date">
                                        <i class="fa fa-calendar"></i>
                                        {{ entry.formatted_date }}
                                    </div>
                                    <div class="timesheet-user">
                                        <i class="fa fa-user"></i>
                                        {{ entry.user.full_name }}
                                    </div>
                                </div>
                                <div class="timesheet-task-name" v-if="entry.task_name">
                                    <i class="fa fa-tag"></i>
                                    {{ entry.task_name }}
                                </div>
                                <div class="timesheet-content">
                                    <div class="timesheet-description">{{ entry.description }}</div>
                                    <div class="timesheet-duration">
                                        <i class="fa fa-clock-o"></i>
                                        {{ entry.formatted_duration }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tab Allegati -->
                <div v-if="subprojectId" v-show="activeTab === 'attachments'" class="tab-content">
                    <div class="attachments-section">
                        <!-- Dropzone -->
                        <div v-if="canModify" class="dropzone" 
                             :class="{ 'is-dragover': isDragover }"
                             @click="$refs.fileInput.click()"
                             @dragover.prevent="isDragover = true"
                             @dragleave.prevent="isDragover = false"
                             @drop.prevent="handleFileDrop">
                            <i class="fa fa-cloud-upload"></i>
                            <p>Trascina uno o più file qui o clicca per selezionare</p>
                            <small>Max 50MB per file - documenti, immagini, archivi, testo</small>
                            <input 
                                ref="fileInput"
                                type="file"
                                multiple
                                style="display: none"
                                @change="handleFileSelect"
                                accept=".pdf,.doc,.docx,.odt,.rtf,.xls,.xlsx,.ods,.csv,.tsv,.ppt,.pptx,.odp,.jpg,.jpeg,.png,.gif,.webp,.svg,.mp4,.mov,.avi,.mkv,.webm,.txt,.md,.json,.xml,.yml,.yaml,.zip,.rar,.7z,.tar,.gz,.tgz"
                            >
                        </div>

                        <!-- Lista Allegati -->
                        <div class="attachments-list">
                            <div v-if="attachments.length === 0" class="empty-state">
                                <i class="fa fa-folder-open"></i>
                                <p>Nessun allegato</p>
                            </div>
                            <div v-for="attachment in attachments" :key="attachment.id" class="attachment-item">
                                <div class="attachment-icon">
                                    <i class="fa" :class="attachment.icon"></i>
                                </div>
                                <div class="attachment-info">
                                    <div class="attachment-name">{{ attachment.file_name }}</div>
                                    <div class="attachment-meta">
                                        {{ attachment.formatted_size }} • {{ attachment.user.full_name }} • {{ attachment.created_at }}
                                    </div>
                                </div>
                                <div class="attachment-actions">
                                    <a :href="attachment.download_url" class="btn-icon" title="Download">
                                        <i class="fa fa-download"></i>
                                    </a>
                                    <button 
                                        v-if="attachment.is_own || canModify"
                                        type="button" 
                                        class="btn-icon btn-delete" 
                                        @click="deleteAttachment(attachment.id)"
                                        title="Elimina">
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Uploading indicator -->
                        <div v-if="uploadProgress.active" class="uploading-indicator">
                            <i class="fa fa-spinner fa-spin"></i>
                            Caricamento {{ uploadProgress.done }}/{{ uploadProgress.total }}...
                        </div>
                        <div v-if="uploadProgress.errors.length > 0 && !uploadProgress.active" class="upload-errors-list">
                            <p class="upload-errors-title">File non caricati:</p>
                            <ul>
                                <li v-for="(err, idx) in uploadProgress.errors" :key="'upload-err-' + idx">
                                    {{ err.file.name }} — {{ err.message }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Input Commento (fuori da sidebar-content per evitare scroll) -->
            <div v-show="activeTab === 'overview'" class="comment-input-container-fixed" :class="{ 'is-expanded': commentEditorFocused }">
                <p v-if="notificationRecipientLabel" class="follower-notice">{{ notificationRecipientLabel }}</p>
                <!-- Placeholder compatto - visibile quando editor non è in focus -->
                <div v-if="!commentEditorFocused" class="comment-placeholder" 
                     :class="{ 'is-dragover': commentDragover }"
                     @click="expandCommentEditor"
                     @dragover.prevent="commentDragover = true"
                     @dragleave.prevent="commentDragover = false"
                     @drop.prevent="handleCommentDrop">
                    <i class="fa fa-pencil"></i>
                    <span>Scrivi un commento...</span>
                </div>
                <!-- Editor completo - visibile al focus -->
                <div v-else class="comment-editor-box">
                    <wysiwyg-editor
                        ref="commentEditor"
                        v-model="newComment"
                        :users="projectUsers"
                        :attachments="attachments"
                        :disabled="isSendingComment"
                        :task-id="subprojectId"
                        mode="comment"
                        @mentioned="handleMention"
                        @submit="sendComment"
                        @image-uploaded="handleImageUploaded"
                        @file-uploaded="handleDescriptionFileUploaded"
                        @preview-image="openImagePreview"
                        @upload-error="showError"
                        @blur="collapseCommentEditor"
                        placeholder="Scrivi un commento..."
                    ></wysiwyg-editor>
                    <button 
                        type="button" 
                        class="comment-send-inline"
                        @mousedown.prevent="sendComment"
                        :disabled="isCommentEmpty || isSendingComment">
                        <i class="fa fa-paper-plane"></i>
                    </button>
                </div>
            </div>

            <!-- Assignee Selector Modal -->
            <div v-if="showAssigneeSelector" class="inline-modal-overlay" @click="closeAssigneeSelector">
                <div class="inline-modal" @click.stop>
                    <div class="inline-modal-header">
                        <h5>Seleziona Assegnatari</h5>
                        <button class="close-btn" @click="closeAssigneeSelector">
                            <i class="fa fa-times"></i>
                        </button>
                    </div>
                    <div class="inline-modal-body">
                        <input 
                            type="text" 
                            v-model="assigneeSearchQuery" 
                            placeholder="Cerca utente..." 
                            class="search-input" 
                            ref="assigneeSearch" />
                        <div class="user-list">
                            <div 
                                v-for="user in filteredProjectUsers" 
                                :key="user.id" 
                                class="user-item"
                                :class="{ selected: isUserAssigned(user.id) }"
                                @click="toggleAssignee(user)">
                                <div class="user-avatar-small">
                                    <img v-if="user.avatar" :src="user.avatar" :alt="user.full_name">
                                    <div v-else class="avatar-initials-small">{{ user.initials }}</div>
                                </div>
                                <span class="user-name">{{ user.full_name }}</span>
                                <i v-if="isUserAssigned(user.id)" class="fa fa-check"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Modale immagine -->
        <div v-if="showImageModal" class="image-modal-overlay" @click="closeImageModal">
            <div class="image-modal-content" @click.stop>
                <div class="image-modal-actions">
                    <button type="button" class="image-modal-download" @click="downloadModalImage" title="Scarica immagine">
                        <i class="fa fa-download"></i>
                    </button>
                    <button type="button" class="image-modal-close" @click="closeImageModal">&times;</button>
                </div>
                <img :src="modalImageUrl" :alt="modalImageName" class="image-modal-img">
                <div class="image-modal-name">{{ modalImageName }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import WysiwygEditor from './WysiwygEditor.vue';

var taskAttachmentUpload = require('../../utils/taskAttachmentUpload');
var uploadTaskAttachments = taskAttachmentUpload.uploadTaskAttachments;
var filesFromFileList = taskAttachmentUpload.filesFromFileList;

export default {
    name: 'TaskDetailSidebar',
    
    components: {
        WysiwygEditor,
    },

    props: {
        subprojectId: {
            type: [Number, String],
            required: false,
            default: null
        },
        projectId: {
            type: [Number, String],
            required: false,
            default: null
        },
        weekStart: {
            type: String,
            required: false,
            default: null
        },
        weekEnd: {
            type: String,
            required: false,
            default: null
        },
        isPlanningMode: {
            type: Boolean,
            default: false
        },
        assignmentData: {
            type: Object,
            required: false,
            default: () => null
        },
        projectNameProp: {
            type: String,
            required: false,
            default: null
        },
        canModify: {
            type: Boolean,
            default: false
        },
        canModifyTaskStatus: {
            type: Boolean,
            default: false
        },
        apiToken: {
            type: String,
            required: false,
            default: ''
        },
        dailyPlanningEnabled: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            isOpen: false,
            subscribedTaskChannelId: null,
            _openedAt: 0,
            activeTab: 'overview',
            // Modale immagine
            showImageModal: false,
            modalImageUrl: '',
            modalImageName: '',
            taskData: {
                name: '',
                description: '',
                status: '',
                priority: '',
                expected_duration: null,
                created_at: null,
                creator: null,
                total_hours: 0,
                planned_hours: null,
                start_date: null,
                end_date: null,
                suggested_end_date: null,
                due_date: null,
                commercial_agreement: '',
                assigned_users: [],
                predecessors: [],
                successors: [],
                project_name: '',
                parent_name: null,
                parent_id: null,
                task_sheet_id: null,
                task_sheets: [],
                show_task_sheet_field: false,
                has_children: false
            },
            descriptionText: '',
            formattedDescription: '',
            descriptionFormat: 'text',
            isSavingDescription: false,
            descriptionSaveTimer: null,
            descriptionLastSaved: '',
            descriptionSaveQueued: false,
            isUploadingDescriptionImage: false,
            isDescriptionDragover: false,
            isDescriptionFocused: false,
            isAddingDep: false,
            depSearchQuery: '',
            depSearchResults: [],
            depSearchTimer: null,
            isEditingDescription: false,
            descriptionDragover: false,
            descriptionDragCounter: 0,
            commentDragover: false,
            pendingDropFiles: null,
            pastedDescriptionImages: [],
            pastedDescriptionTable: null, // HTML della tabella incollata
            comments: [],
            activities: [],
            activityFilter: 'comments',
            notificationRecipientCount: 0,
            isFollowing: false,
            taskProjectId: null,
            newComment: '',
            commentHasTable: false,
            mentionedUsers: [],
            editingCommentBaselineMentions: [],
            editingMentionedUsers: [],
            isSendingComment: false,
            commentEditorFocused: false,
            editingCommentId: null,
            editingCommentText: '',
            editingCommentLastSaved: '',
            commentSaveTimer: null,
            commentSaveQueued: false,
            isSavingComment: false,
            attachments: [],
            uploadProgress: {
                active: false,
                done: 0,
                total: 0,
                errors: [],
            },
            isDragover: false,
            projectUsers: [],
            echo: null,
            timesheetEntries: [],
            isLoadingTimesheet: false,
            isLoadingTask: false, // Loading state per cambio task
            taskDetailsRequestId: 0,
            
            // Inline editing
            editingFields: {
                name: '',
                expected_duration: null,
                start_date: null,
                end_date: null,
                due_date: null,
                priority: '',
                commercial_agreement: '',
                assigned_users: [],
                task_sheet_id: null
            },
            fieldSaveTimers: {},
            showPrioritySelector: false,
            showTypeSelector: false,
            showAssigneeSelector: false,
            selectorPosition: { top: 0, left: 0 },
            assigneeSearchQuery: '',
            isEditingStartDate: false,
            isEditingEndDate: false,
            isEditingDueDate: false,
            
            // Assignment editing
            editingAssignment: {
                newProjectId: null,
                newProjectName: '',
                newSubprojectId: null,
                newSubprojectName: '',
                newUserId: null,
                newUserName: '',
                newHours: null,
                newDescription: '',
                specificDate: null
            },
            availableProjects: [],
            availableSubprojects: [],
            availableUsers: [],
            filteredProjects: [],
            filteredSubprojects: [],
            isLoadingProjects: false,
            isLoadingSubprojects: false,
            isSavingAssignment: false,
            projectSearchQuery: '',
            subprojectSearchQuery: '',
            userSearchQuery: '',
            showProjectDropdown: false,
            showSubprojectDropdown: false,
            showUserDropdown: false,
            filteredUsers: [],
            selectedProjectIndex: -1,
            selectedSubprojectIndex: -1,
            selectedUserIndex: -1,
            // Creazione rapida task
            creatingSubtaskForParentId: null,
            newSubtaskName: '',
            isCreatingTask: false,
            isCreatingSubtask: false,
            /** Snapshot form nuova assegnazione (pianificazione) per conferma chiusura */
            planningCreationBaseline: null,
        };
    },

    methods: {
        // Previeni scroll della pagina principale quando si scrolla la sidebar
        preventParentScroll(e) {
            const target = e.currentTarget;
            const scrollTop = target.scrollTop;
            const scrollHeight = target.scrollHeight;
            const height = target.clientHeight;
            const wheelDelta = e.deltaY;
            const isDeltaPositive = wheelDelta > 0;

            // Se si scrolla verso il basso e si è già in fondo
            if (isDeltaPositive && scrollTop + height >= scrollHeight) {
                e.preventDefault();
                return;
            }

            // Se si scrolla verso l'alto e si è già in cima
            if (!isDeltaPositive && scrollTop <= 0) {
                e.preventDefault();
                return;
            }
        },
        
        async open() {
            this.isOpen = true;
            this._openedAt = Date.now();
            document.documentElement.classList.add('task-sidebar-mobile-open');

            if (window.innerWidth > 768) {
                document.body.classList.add('task-sidebar-push-open');
            }
            
            // Se abbiamo il nome del progetto dalla prop, impostalo subito
            if (this.projectNameProp) {
                this.taskData.project_name = this.projectNameProp;
                console.log('📁 Nome progetto impostato da prop:', this.projectNameProp);
            }
            
            // Se siamo in planning mode, apri la tab pianificazione
            if (this.isPlanningMode && this.assignmentData) {
                this.activeTab = 'planning';
            }
            
            // Se c'è un subproject_id, carica i dettagli del task
            // Altrimenti apri direttamente sulla tab timesheet (solo project)
            if (this.subprojectId) {
                await this.loadTaskDetails();
                this.connectToChannel();
            } else if (!this.isPlanningMode) {
                // Solo project (non planning): mostra direttamente timesheet
                this.activeTab = 'timesheet';
                await this.loadTimesheetData();
            }
            
            // Carica i timesheet in background per aggiornare il conteggio
            // (non aspettiamo il risultato per non bloccare l'apertura)
            if (this.isPlanningMode || this.subprojectId) {
                this.preloadTimesheetData();
            }
        },

        handleOverlayClick(event) {
            // Verifica se il click è su un elemento della grid dei task
            // In quel caso, non chiudere la sidebar - sarà il click handler del task a gestire l'apertura del nuovo task
            const target = event.target;
            
            // Controlla se il click è su un task row o un suo figlio
            const isTaskRow = target.closest('.main-task-row, .subtask-row, tr[data-id]');
            const isTaskGrid = target.closest('.subprojects-grid');
            const isTaskLink = target.closest('[data-task-id]');
            
            if (isTaskRow || isTaskGrid || isTaskLink) {
                console.log('🔵 Click su task nella grid, non chiudo la sidebar');
                // Non fare nulla - il click handler del task gestirà l'apertura
                return;
            }
            
            // Altrimenti chiudi la sidebar
            console.log('🔵 Click fuori dalla sidebar, chiudo');
            this.close();
        },

        capturePlanningCreationBaseline() {
            if (!this.isPlanningMode || !this.assignmentData) {
                this.planningCreationBaseline = null;

                return;
            }
            const planningId = this.assignmentData.planning_id;
            if (planningId !== null && planningId !== undefined) {
                this.planningCreationBaseline = null;

                return;
            }
            const a = this.editingAssignment;
            this.planningCreationBaseline = JSON.stringify({
                newProjectId: a.newProjectId != null ? a.newProjectId : null,
                newSubprojectId: a.newSubprojectId != null ? a.newSubprojectId : null,
                newUserId: a.newUserId != null ? a.newUserId : null,
                newHours: a.newHours != null ? a.newHours : null,
                newDescription: (a.newDescription || '').trim(),
                specificDate: a.specificDate != null ? a.specificDate : null,
            });
        },

        hasUnsavedPlanningCreationChanges() {
            if (!this.planningCreationBaseline) {
                return false;
            }
            const a = this.editingAssignment;
            const current = JSON.stringify({
                newProjectId: a.newProjectId != null ? a.newProjectId : null,
                newSubprojectId: a.newSubprojectId != null ? a.newSubprojectId : null,
                newUserId: a.newUserId != null ? a.newUserId : null,
                newHours: a.newHours != null ? a.newHours : null,
                newDescription: (a.newDescription || '').trim(),
                specificDate: a.specificDate != null ? a.specificDate : null,
            });

            return current !== this.planningCreationBaseline;
        },

        shouldConfirmDiscardPlanningCreation() {
            if (!this.isPlanningMode || !this.assignmentData) {
                return false;
            }
            const planningId = this.assignmentData.planning_id;
            if (planningId !== null && planningId !== undefined) {
                return false;
            }

            return this.hasUnsavedPlanningCreationChanges();
        },

        copyTaskLink() {
            var projectId = this.taskProjectId || this.projectIdProp;
            if (!projectId || !this.subprojectId) {
                return;
            }
            var url = window.location.origin + '/projects/' + projectId + '?open_task=' + this.subprojectId;
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(url);
            }
        },

        async toggleFollow() {
            if (!this.subprojectId) {
                return;
            }
            var method = this.isFollowing ? 'delete' : 'post';
            try {
                var response = await axios({ method: method, url: '/tasks/' + this.subprojectId + '/follow' });
                if (response.data && response.data.success) {
                    this.isFollowing = !!response.data.is_following;
                    this.notificationRecipientCount = response.data.notification_recipient_count || 0;
                }
            } catch (error) {
                this.showError('Impossibile aggiornare il seguito del task');
            }
        },

        async close(forceClose) {
            const force = forceClose === true;

            if (!force && this.shouldConfirmDiscardPlanningCreation()) {
                const SwalApi = typeof Swal !== 'undefined' ? Swal : window.Swal;
                const result = await SwalApi.fire({
                    title: 'Chiudere senza salvare?',
                    text: 'Vuoi davvero chiudere? Perderai tutte le modifiche alla nuova assegnazione.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Sì, chiudi',
                    cancelButtonText: 'Annulla',
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#666',
                });
                if (!result.isConfirmed) {
                    return;
                }
            }

            // Salva tutti i campi pending prima di chiudere
            Object.keys(this.fieldSaveTimers).forEach(field => {
                this.saveField(field);
            });

            // Flush autosave descrizione / commento in edit (await per non perdere dati)
            if (!this.isPlanningMode && this.canModify) {
                await this.flushPendingRichTextSaves(this.subprojectId);
            }
            
            // Chiudi tutti i dropdown
            this.closePrioritySelector();
            this.closeTypeSelector();
            this.closeAssigneeSelector();
            
            // Rimuovi il focus dall'elemento attivo per permettere la riapertura
            // della sidebar cliccando sulla stessa riga
            if (document.activeElement && document.activeElement !== document.body) {
                document.activeElement.blur();
            }
            
            this.commentEditorFocused = false;
            this.isEditingDescription = false;
            this.clearRichTextSaveTimers();
            if (this.$refs.descriptionEditor) {
                this.$refs.descriptionEditor.clear();
            }
            this.disconnectFromChannel();
            this.isOpen = false;
            document.documentElement.classList.remove('task-sidebar-mobile-open');
            document.body.classList.remove('task-sidebar-push-open');
            this.planningCreationBaseline = null;
            this.$emit('close');
        },

        prepareTaskSwitch() {
            this.clearRichTextSaveTimers();
            this.isEditingDescription = false;
            this.editingCommentId = null;
            this.isLoadingTask = true;
            this.taskData.name = '';
            this.taskData.status = '';
            this.descriptionText = '';
            this.descriptionLastSaved = '';
            this.formattedDescription = '';
            this.descriptionFormat = 'text';
            this.comments = [];
            this.activities = [];
            this.notificationRecipientCount = 0;
            this.isFollowing = false;
            this.taskProjectId = null;
            this.attachments = [];
            this.timesheetEntries = [];
            this.newComment = '';
            this.mentionedUsers = [];
            this.pastedDescriptionImages = [];
            this.commentEditorFocused = false;
            this.editingCommentText = '';
            this.editingCommentLastSaved = '';
            this.editingCommentBaselineMentions = [];
            this.editingMentionedUsers = [];
            if (this.$refs.descriptionEditor) {
                this.$refs.descriptionEditor.clear();
            }
        },

        async reloadCurrentTask(previousTaskId) {
            if (!this.subprojectId || !this.isOpen) {
                return;
            }

            const flushTaskId = previousTaskId || this.subprojectId;
            if (this.canModify && !this.isPlanningMode) {
                await this.flushPendingRichTextSaves(flushTaskId);
            }

            this.prepareTaskSwitch();
            this.disconnectFromChannel();
            await this.loadTaskDetails();
            this.connectToChannel();
            this.preloadTimesheetData();
        },

        async loadTaskDetails() {
            const requestedTaskId = Number(this.subprojectId);
            if (!requestedTaskId || requestedTaskId !== requestedTaskId) {
                return;
            }

            const requestId = ++this.taskDetailsRequestId;
            this.isLoadingTask = true;
            this.pastedDescriptionImages = [];
            this.commentEditorFocused = false;
            this.isEditingDescription = false;
            if (this.$refs.descriptionEditor) {
                this.$refs.descriptionEditor.clear();
            }
            
            try {
                const response = await axios.get('/tasks/' + requestedTaskId + '/detail');
                if (requestId !== this.taskDetailsRequestId || Number(this.subprojectId) !== requestedTaskId) {
                    return;
                }
                if (response.data.success) {
                    const data = response.data.data;
                    this.taskData = {
                        name: data.name,
                        code: data.code || null,
                        description: data.description || '',
                        status: data.status,
                        priority: data.priority,
                        expected_duration: data.expected_duration,
                        planned_hours: data.planned_hours,
                        total_hours: data.total_hours || 0,
                        start_date: data.start_date,
                        end_date: data.end_date,
                        suggested_end_date: data.suggested_end_date,
                        due_date: data.due_date,
                        commercial_agreement: data.commercial_agreement,
                        assigned_users: data.assigned_users || [],
                        predecessors: data.predecessors || [],
                        successors: data.successors || [],
                        project_name: this.projectNameProp || data.project_name,
                        parent_name: data.parent_name,
                        parent_id: data.parent_id,
                        task_sheet_id: data.task_sheet_id,
                        task_sheets: data.task_sheets || [],
                        show_task_sheet_field: data.show_task_sheet_field || false,
                        created_at: data.created_at,
                        creator: data.creator,
                        has_children: data.has_children || false
                    };
                    this.descriptionText = data.description || '';
                    this.formattedDescription = data.formatted_description || '';
                    this.descriptionFormat = data.description_format || 'text';
                    this.comments = data.comments;
                    this.activities = data.activities || [];
                    this.notificationRecipientCount = data.notification_recipient_count || 0;
                    this.isFollowing = !!data.is_following;
                    this.taskProjectId = data.project_id || this.projectIdProp || null;
                    this.attachments = data.attachments;

                    // Utenti per @menzioni: stesso payload di GET /projects/{id}/users, senza race sulla seconda chiamata
                    if (data.mention_users && data.mention_users.length > 0) {
                        this.projectUsers = data.mention_users;
                    }

                    // Carica preview immagini dalla descrizione
                    this.loadDescriptionImagePreviews();
                    this.descriptionLastSaved = this.descriptionText || '';

                    // Inizializza campi di editing
                    this.editingFields = {
                        name: data.name,
                        expected_duration: data.expected_duration,
                        start_date: data.start_date,
                        end_date: data.end_date,
                        due_date: data.due_date,
                        priority: data.priority,
                        commercial_agreement: data.commercial_agreement,
                        assigned_users: data.assigned_users || [],
                        task_sheet_id: data.task_sheet_id
                    };

                    // Scorri alla fine della chat se ci sono commenti
                    this.$nextTick(() => {
                        this.scrollToBottom();
                    });
                }
            } catch (error) {
                if (requestId === this.taskDetailsRequestId) {
                    var status = error.response && error.response.status;
                    if (this.isPlanningMode && status === 403) {
                        return;
                    }
                    console.error('Errore caricamento dettagli task:', error);
                    this.showError('Errore nel caricamento dei dettagli del task');
                }
            } finally {
                if (requestId === this.taskDetailsRequestId) {
                    this.isLoadingTask = false;
                }
            }
        },

        /**
         * Sanitizza e formatta l'HTML della tabella
         * - Rimuove stili inline
         * - Converte la prima riga in intestazione (th)
         */
        sanitizeTableHtml(html) {
            // Crea un elemento temporaneo per manipolare l'HTML
            var temp = document.createElement('div');
            temp.innerHTML = html;
            
            var table = temp.querySelector('table');
            if (!table) return html;
            
            // Rimuovi tutti gli attributi style, class, e altri non necessari
            var elements = temp.querySelectorAll('*');
            for (var i = 0; i < elements.length; i++) {
                var el = elements[i];
                // Mantieni solo colspan e rowspan
                var colspan = el.getAttribute('colspan');
                var rowspan = el.getAttribute('rowspan');
                
                // Rimuovi tutti gli attributi
                while (el.attributes.length > 0) {
                    el.removeAttribute(el.attributes[0].name);
                }
                
                // Ripristina colspan e rowspan se c'erano
                if (colspan) el.setAttribute('colspan', colspan);
                if (rowspan) el.setAttribute('rowspan', rowspan);
            }
            
            // Se non c'è già un thead, converti la prima riga in intestazione
            if (!table.querySelector('thead')) {
                var firstRow = table.querySelector('tr');
                if (firstRow) {
                    // Crea thead
                    var thead = document.createElement('thead');
                    var tbody = table.querySelector('tbody') || document.createElement('tbody');
                    
                    // Converti i td della prima riga in th
                    var cells = firstRow.querySelectorAll('td');
                    for (var j = 0; j < cells.length; j++) {
                        var th = document.createElement('th');
                        th.innerHTML = cells[j].innerHTML;
                        if (cells[j].getAttribute('colspan')) {
                            th.setAttribute('colspan', cells[j].getAttribute('colspan'));
                        }
                        if (cells[j].getAttribute('rowspan')) {
                            th.setAttribute('rowspan', cells[j].getAttribute('rowspan'));
                        }
                        cells[j].parentNode.replaceChild(th, cells[j]);
                    }
                    
                    // Sposta la prima riga nel thead
                    thead.appendChild(firstRow);
                    
                    // Se non c'era tbody, crea uno e sposta le righe rimanenti
                    if (!table.querySelector('tbody')) {
                        var rows = table.querySelectorAll('tr');
                        for (var k = 0; k < rows.length; k++) {
                            tbody.appendChild(rows[k]);
                        }
                    }
                    
                    // Ricostruisci la tabella
                    table.innerHTML = '';
                    table.appendChild(thead);
                    table.appendChild(tbody);
                }
            }
            
            return temp.innerHTML;
        },

        /**
         * Rimuove la tabella incollata dalla descrizione
         */
        removeDescriptionTable() {
            this.pastedDescriptionTable = null;
        },

        /**
         * Entra in modalità editing per la descrizione
         * Usato quando si clicca sulla view HTML della descrizione
         */
        handleDescriptionViewClick(e) {
            // Click su immagine inline -> apri preview
            if (e.target.tagName === 'IMG' && e.target.classList.contains('comment-inline-image')) {
                e.preventDefault();
                e.stopPropagation();
                this.modalImageUrl = e.target.src;
                this.modalImageName = e.target.alt || '';
                this.showImageModal = true;
                return;
            }
            // Click su link (inclusi file download)
            var link = e.target.closest('a');
            if (link && link.href) {
                e.preventDefault();
                e.stopPropagation();
                window.open(link.href, '_blank', 'noopener,noreferrer');
                return;
            }
            // Click su container file/immagine -> non entrare in editing
            if (e.target.closest('.comment-file-container') || e.target.closest('.comment-image-container')) {
                return;
            }
            this.startEditingDescription();
        },

        startEditingDescription() {
            if (!this.canModify) return;
            
            this.isEditingDescription = true;
            this.$nextTick(() => {
                if (this.$refs.descriptionEditor) {
                    this.$refs.descriptionEditor.focus();
                    if (this.pendingDropFiles && this.pendingDropFiles.length) {
                        this.$refs.descriptionEditor.uploadFiles(this.pendingDropFiles);
                        this.pendingDropFiles = null;
                    }
                }
            });
        },

        handleDescriptionAreaDragover(e) {
            if (!this.canModify) return;
            this.descriptionDragCounter++;
            this.descriptionDragover = true;
        },

        handleDescriptionAreaDragleave(e) {
            this.descriptionDragCounter--;
            if (this.descriptionDragCounter <= 0) {
                this.descriptionDragCounter = 0;
                this.descriptionDragover = false;
            }
        },

        handleDescriptionDrop(e) {
            this.descriptionDragover = false;
            this.descriptionDragCounter = 0;
            var files = filesFromFileList(e.dataTransfer.files);
            if (!files.length || !this.canModify) return;
            this.handleDescriptionFilesDropped(files);
        },

        isImageAttachment(attachment) {
            return attachment.mime_type && attachment.mime_type.indexOf('image/') === 0;
        },

        handleGalleryItemClick(attachment) {
            if (this.isImageAttachment(attachment)) {
                this.modalImageUrl = attachment.download_url;
                this.modalImageName = attachment.file_name;
                this.showImageModal = true;
            } else {
                window.open(attachment.download_url, '_blank');
            }
        },

        handleCommentDrop(e) {
            this.commentDragover = false;
            var files = filesFromFileList(e.dataTransfer.files);
            if (!files.length) {
                return;
            }
            this.commentEditorFocused = true;
            this.$nextTick(() => {
                if (this.$refs.commentEditor) {
                    this.$refs.commentEditor.focus();
                    this.$refs.commentEditor.uploadFiles(files);
                }
            });
        },

        /**
         * Termina la modalità editing e salva la descrizione
         */
        finishEditingDescription() {
            this.isEditingDescription = false;
            this.saveDescription();
        },

        /**
         * Handler per blur della textarea descrizione
         * Ritarda la chiusura per permettere click sui bottoni toolbar
         */
        handleDescriptionBlur(event) {
            // Ritarda per permettere click sui bottoni della toolbar
            setTimeout(() => {
                // Verifica se il focus è ancora dentro la sezione descrizione
                const activeEl = document.activeElement;
                const descSection = this.$el.querySelector('.description-section');
                if (!descSection || !descSection.contains(activeEl)) {
                    this.isDescriptionFocused = false;
                    this.finishEditingDescription();
                }
            }, 150);
        },

        /**
         * Handler per shortcut tastiera nella descrizione
         */
        handleDescriptionKeydown(event) {
            // Cmd/Ctrl + B = Bold
            if ((event.metaKey || event.ctrlKey) && event.key === 'b') {
                event.preventDefault();
                this.insertMarkdownBold();
            }
            // Cmd/Ctrl + I = Italic
            if ((event.metaKey || event.ctrlKey) && event.key === 'i') {
                event.preventDefault();
                this.insertMarkdownItalic();
            }
        },

        /**
         * Inserisce markup Markdown attorno al testo selezionato o al cursore
         */
        insertMarkdownWrapper(before, after) {
            const textarea = this.$refs.descriptionTextarea;
            if (!textarea) return;
            
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = this.descriptionText;
            const selectedText = text.substring(start, end);
            
            const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
            this.descriptionText = newText;
            
            // Riposiziona cursore
            this.$nextTick(() => {
                textarea.focus();
                if (selectedText) {
                    // Se c'era selezione, seleziona il testo formattato
                    textarea.setSelectionRange(start + before.length, end + before.length);
                } else {
                    // Altrimenti posiziona il cursore tra i marker
                    textarea.setSelectionRange(start + before.length, start + before.length);
                }
            });
        },

        /**
         * Inserisce una nuova riga con prefisso (per liste)
         */
        insertMarkdownLinePrefix(prefix) {
            const textarea = this.$refs.descriptionTextarea;
            if (!textarea) return;
            
            const start = textarea.selectionStart;
            const text = this.descriptionText;
            
            // Trova l'inizio della riga corrente
            let lineStart = text.lastIndexOf('\n', start - 1) + 1;
            
            // Inserisci il prefisso all'inizio della riga
            const newText = text.substring(0, lineStart) + prefix + text.substring(lineStart);
            this.descriptionText = newText;
            
            this.$nextTick(() => {
                textarea.focus();
                textarea.setSelectionRange(start + prefix.length, start + prefix.length);
            });
        },

        /**
         * Inserisce grassetto **testo**
         */
        insertMarkdownBold() {
            this.insertMarkdownWrapper('**', '**');
        },

        /**
         * Inserisce corsivo *testo*
         */
        insertMarkdownItalic() {
            this.insertMarkdownWrapper('*', '*');
        },

        /**
         * Inserisce elemento lista puntata
         */
        insertMarkdownBulletList() {
            this.insertMarkdownLinePrefix('- ');
        },

        /**
         * Inserisce elemento lista numerata
         */
        insertMarkdownNumberedList() {
            this.insertMarkdownLinePrefix('1. ');
        },

        /**
         * Inserisce link Markdown [testo](url)
         */
        insertMarkdownLink() {
            const textarea = this.$refs.descriptionTextarea;
            if (!textarea) return;
            
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = this.descriptionText;
            const selectedText = text.substring(start, end);
            
            // Se c'è testo selezionato, usalo come testo del link
            if (selectedText) {
                const newText = text.substring(0, start) + '[' + selectedText + '](url)' + text.substring(end);
                this.descriptionText = newText;
                
                this.$nextTick(() => {
                    textarea.focus();
                    // Seleziona "url" per permettere all'utente di sostituirlo
                    const urlStart = start + selectedText.length + 3;
                    textarea.setSelectionRange(urlStart, urlStart + 3);
                });
            } else {
                // Nessuna selezione, inserisci template vuoto
                const linkTemplate = '[testo](url)';
                const newText = text.substring(0, start) + linkTemplate + text.substring(end);
                this.descriptionText = newText;
                
                this.$nextTick(() => {
                    textarea.focus();
                    // Seleziona "testo" per permettere all'utente di sostituirlo
                    textarea.setSelectionRange(start + 1, start + 6);
                });
            }
        },

        isRichTextEmpty(html) {
            if (!html) {
                return true;
            }
            if (/data-attachment-id=|\[(?:img|video|file):\d+\]|<img\b|<video\b/.test(html)) {
                return false;
            }
            var stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
            return stripped.length === 0;
        },

        editorByRef(name) {
            var editor = this.$refs[name];
            if (Array.isArray(editor)) {
                editor = editor[0];
            }
            return editor || null;
        },

        mergeImportedAttachments(list) {
            var self = this;
            (list || []).forEach(function(attachment) {
                if (!attachment || !attachment.id) {
                    return;
                }
                if (!self.attachments.some(function(item) { return item.id === attachment.id; })) {
                    self.attachments.unshift(attachment);
                }
            });
        },

        handleDescriptionBlurWysiwyg() {
            this.isEditingDescription = false;
            this.saveDescription();
        },

        handleDescriptionImageUploaded() {
            var editor = this.$refs.descriptionEditor;
            if (editor && typeof editor.getHtml === 'function') {
                this.descriptionText = editor.getHtml();
            }
            this.saveDescription();
        },

        handleDescriptionFilePasted(files) {
            var list = Array.isArray(files) ? files : [files];
            this.handleDescriptionFilesDropped(list);
        },

        handleDescriptionFilesDropped(files) {
            if (!files || !files.length || !this.canModify) {
                return;
            }

            if (this.isEditingDescription && this.$refs.descriptionEditor) {
                this.$refs.descriptionEditor.uploadFiles(files);
                return;
            }

            this.pendingDropFiles = files;
            this.startEditingDescription();
        },

        handleDescriptionFileUploaded(attachment) {
            if (!attachment || !attachment.id) {
                return;
            }

            if (!this.attachments.some(function(a) { return a.id === attachment.id; })) {
                this.attachments.unshift(attachment);
            }
        },

        clearRichTextSaveTimers() {
            if (this.descriptionSaveTimer) {
                clearTimeout(this.descriptionSaveTimer);
                this.descriptionSaveTimer = null;
            }
            if (this.commentSaveTimer) {
                clearTimeout(this.commentSaveTimer);
                this.commentSaveTimer = null;
            }
            this.descriptionSaveQueued = false;
            this.commentSaveQueued = false;
        },

        debounceSaveDescription() {
            if (!this.isEditingDescription || !this.canModify || this.isPlanningMode) {
                return;
            }

            if (this.descriptionSaveTimer) {
                clearTimeout(this.descriptionSaveTimer);
            }

            this.descriptionSaveTimer = setTimeout(() => {
                this.descriptionSaveTimer = null;
                this.saveDescription();
            }, 1500);
        },

        /**
         * Flush immediato di descrizione e commento in edit verso un taskId esplicito
         * (necessario al cambio task: subprojectId è già quello nuovo).
         */
        async flushPendingRichTextSaves(taskId) {
            const descriptionEditor = this.editorByRef('descriptionEditor');
            if (descriptionEditor && descriptionEditor.waitForUploads) {
                await descriptionEditor.waitForUploads();
                if (typeof descriptionEditor.getHtml === 'function') {
                    this.descriptionText = descriptionEditor.getHtml();
                }
            }
            const editEditor = this.editorByRef('editCommentEditor');
            if (editEditor && editEditor.waitForUploads) {
                await editEditor.waitForUploads();
                if (typeof editEditor.getHtml === 'function') {
                    this.editingCommentText = editEditor.getHtml();
                }
            }

            if (this.descriptionSaveTimer) {
                clearTimeout(this.descriptionSaveTimer);
                this.descriptionSaveTimer = null;
            }
            if (this.commentSaveTimer) {
                clearTimeout(this.commentSaveTimer);
                this.commentSaveTimer = null;
            }

            if (taskId && this.canModify && !this.isPlanningMode) {
                await this.saveDescription(taskId);
            }

            if (this.editingCommentId) {
                await this.saveEditComment(this.editingCommentId, { keepEditing: true });
            }
        },

        async saveDescription(taskId) {
            const targetId = taskId || this.subprojectId;
            if (!targetId) {
                console.log('⚠️ saveDescription: subprojectId non valido, skip');
                return;
            }
            if (!this.canModify || this.isPlanningMode) {
                return;
            }

            const descriptionEditor = this.editorByRef('descriptionEditor');
            if (descriptionEditor && descriptionEditor.uploadProgress && descriptionEditor.uploadProgress.active) {
                this.descriptionSaveTimer = setTimeout(() => {
                    this.descriptionSaveTimer = null;
                    this.saveDescription(targetId);
                }, 300);
                return;
            }

            if (this.descriptionSaveTimer) {
                clearTimeout(this.descriptionSaveTimer);
                this.descriptionSaveTimer = null;
            }

            const payload = this.descriptionText || '';
            if (payload === this.descriptionLastSaved) {
                return;
            }

            if (this.isSavingDescription) {
                this.descriptionSaveQueued = true;
                return;
            }

            this.isSavingDescription = true;
            try {
                const response = await axios.post(`/tasks/${targetId}/description`, {
                    description: payload,
                    description_format: 'html'
                });

                if (response.data.success) {
                    this.descriptionLastSaved = payload;
                    // Aggiorna taskData solo se siamo ancora sullo stesso task
                    if (Number(this.subprojectId) === Number(targetId)) {
                        this.taskData.description = response.data.description;
                        this.descriptionFormat = 'html';
                        if (response.data.formatted_description !== undefined) {
                            this.formattedDescription = response.data.formatted_description;
                        }
                        this.mergeImportedAttachments(response.data.imported_attachments);
                    }
                }
            } catch (error) {
                console.error('Errore salvataggio descrizione:', error);
                this.showError('Errore nel salvataggio della descrizione');
            } finally {
                this.isSavingDescription = false;
                if (this.descriptionSaveQueued) {
                    this.descriptionSaveQueued = false;
                    if ((this.descriptionText || '') !== this.descriptionLastSaved) {
                        await this.saveDescription(targetId);
                    }
                }
            }
        },

        async savePlanningDescription() {
            if (this.isSavingDescription || !this.assignmentData || !this.assignmentData.planning_id) return;

            this.isSavingDescription = true;
            try {
                await axios.post(`/admin/developers-planning/${this.assignmentData.planning_id}/description`, {
                    description: this.assignmentData.description
                });
                // Nessun messaggio in caso di successo
            } catch (error) {
                console.error('Errore salvataggio note pianificazione:', error);
                this.showError('Errore nel salvataggio delle note');
            } finally {
                this.isSavingDescription = false;
            }
        },

        handleFileSelect(event) {
            var files = filesFromFileList(event.target.files);
            if (files.length) {
                this.uploadAttachmentFiles(files);
            }
            event.target.value = '';
        },

        handleFileDrop(event) {
            this.isDragover = false;
            var files = filesFromFileList(event.dataTransfer.files);
            if (files.length) {
                this.uploadAttachmentFiles(files);
            }
        },

        async uploadAttachmentFiles(files) {
            if (!this.subprojectId || !files || !files.length) {
                return;
            }

            var self = this;

            this.uploadProgress = {
                active: true,
                done: 0,
                total: files.length,
                errors: [],
            };

            try {
                var result = await uploadTaskAttachments({
                    taskId: this.subprojectId,
                    files: files,
                    concurrency: 3,
                    onProgress: function(progress) {
                        self.uploadProgress = {
                            active: progress.active,
                            done: progress.done,
                            total: progress.total,
                            errors: progress.errors || [],
                        };
                    },
                });

                result.succeeded.forEach(function(item) {
                    if (!self.attachments.some(function(a) { return a.id === item.attachment.id; })) {
                        self.attachments.unshift(item.attachment);
                    }
                });

                if (result.failed.length > 0) {
                    var names = result.failed.map(function(f) { return f.file.name; }).join(', ');
                    self.showError('Alcuni file non sono stati caricati: ' + names);
                }
            } catch (error) {
                console.error('Errore upload file:', error);
                self.showError('Errore nel caricamento dei file');
            } finally {
                self.uploadProgress = {
                    active: false,
                    done: 0,
                    total: 0,
                    errors: self.uploadProgress.errors || [],
                };
            }
        },

        async deleteAttachment(attachmentId) {
            // Rimozione diretta senza conferma per velocità
            try {
                const response = await axios.delete(`/tasks/attachments/${attachmentId}`);
                if (response.data.success) {
                    this.attachments = this.attachments.filter(a => a.id !== attachmentId);
                    // Nessun feedback per successo
                }
            } catch (error) {
                console.error('Errore eliminazione allegato:', error);
                this.showError('Errore nell\'eliminazione dell\'allegato');
            }
        },

        handleGlobalPaste(event) {
            if (!this.isOpen || !this.subprojectId || !this.canModify) return;

            // Non intercettare se il focus è su un input/textarea/contenteditable
            var activeEl = document.activeElement;
            if (activeEl) {
                var tag = activeEl.tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA' || activeEl.isContentEditable) return;
            }

            // Non intercettare se l'editor WYSIWYG è attivo
            if (this.isEditingDescription) return;

            var clipboardData = event.clipboardData;
            if (!clipboardData) return;

            var items = clipboardData.items;
            if (!items) return;

            var pastedFiles = [];
            for (var i = 0; i < items.length; i++) {
                var item = items[i];
                if (item.kind !== 'file') {
                    continue;
                }

                var file = item.getAsFile();
                if (!file) {
                    continue;
                }

                if (item.type.indexOf('image/') === 0 && (!file.name || file.name === 'image.png')) {
                    var extension = file.type.split('/')[1] || 'png';
                    var fileName = 'immagine_' + Date.now() + '_' + i + '.' + extension;
                    file = new File([file], fileName, { type: file.type });
                }

                pastedFiles.push(file);
            }

            if (pastedFiles.length) {
                event.preventDefault();
                this.handleDescriptionFilesDropped(pastedFiles);
            }
        },

        // Handler per immagini caricate dal MentionInput (commenti)
        handleImageUploaded(attachment) {
            // Aggiungi l'immagine alla lista allegati
            this.attachments.unshift(attachment);
        },

        handleMention(users) {
            this.mentionedUsers = users;
        },

        handleEditMention(users) {
            this.editingMentionedUsers = users;
        },

        expandCommentEditor() {
            this.commentEditorFocused = true;
            this.$nextTick(() => {
                if (this.$refs.commentEditor) {
                    this.$refs.commentEditor.focus();
                }
            });
        },

        collapseCommentEditor() {
            if (this.newComment && this.newComment.trim() !== '' && this.newComment !== '<p><br></p>') {
                return;
            }
            this.commentEditorFocused = false;
        },

        async sendComment() {
            var editor = this.$refs.commentEditor;
            if (!editor || this.isSendingComment) {
                return;
            }
            if (editor.waitForUploads) {
                await editor.waitForUploads();
            }
            if (editor.isEmpty()) {
                return;
            }

            var taskId = this.subprojectId;
            var commentBody = typeof editor.getHtml === 'function' ? editor.getHtml() : this.newComment;
            var mentionedUsers = editor.getMentionedUserIds
                ? editor.getMentionedUserIds()
                : (this.mentionedUsers || []).slice();

            this.isSendingComment = true;
            try {
                const response = await axios.post(`/tasks/${taskId}/comments`, {
                    comment: commentBody,
                    format: 'html',
                    mentioned_users: mentionedUsers
                });

                if (Number(this.subprojectId) !== Number(taskId)) {
                    return;
                }

                if (response.data.success) {
                    this.comments.push(response.data.comment);
                    if (typeof response.data.notification_recipient_count === 'number') {
                        this.notificationRecipientCount = response.data.notification_recipient_count;
                    }
                    this.mergeImportedAttachments(response.data.imported_attachments);
                    this.mentionedUsers = [];

                    editor.clear();
                    this.newComment = '';
                    this.commentEditorFocused = false;
                    
                    this.$nextTick(() => {
                        this.scrollToBottom();
                    });
                }
            } catch (error) {
                console.error('Errore invio commento:', error);
                this.showError('Errore nell\'invio del commento');
            } finally {
                this.isSendingComment = false;
            }
        },

        async deleteComment(commentId) {
            // Rimozione diretta senza conferma per velocità
            try {
                const response = await axios.delete(`/tasks/comments/${commentId}`);
                if (response.data.success) {
                    this.comments = this.comments.filter(c => c.id !== commentId);
                    // Nessun feedback per successo
                }
            } catch (error) {
                console.error('Errore eliminazione commento:', error);
                this.showError('Errore nell\'eliminazione del commento');
            }
        },

        reactionTooltip(comment) {
            var users = comment.reacted_users || [];
            if (users.length === 0) {
                return 'Mi piace';
            }
            return users.join(', ');
        },

        async toggleReaction(comment) {
            if (comment._reactingInProgress) {
                return;
            }

            // Aggiornamento ottimistico
            var previousHasReacted = comment.has_reacted;
            var previousCount = comment.reactions_count;
            var previousUsers = (comment.reacted_users || []).slice();
            this.$set(comment, 'has_reacted', !previousHasReacted);
            this.$set(comment, 'reactions_count', previousHasReacted ? previousCount - 1 : previousCount + 1);
            this.$set(comment, '_reactingInProgress', true);

            try {
                var response = await axios.post('/tasks/comments/' + comment.id + '/react');
                if (response.data.success) {
                    this.$set(comment, 'has_reacted', response.data.has_reacted);
                    this.$set(comment, 'reactions_count', response.data.reactions_count);
                    this.$set(comment, 'reacted_users', response.data.reacted_users || []);
                }
            } catch (error) {
                // Rollback
                this.$set(comment, 'has_reacted', previousHasReacted);
                this.$set(comment, 'reactions_count', previousCount);
                this.$set(comment, 'reacted_users', previousUsers);
                console.error('Errore toggle reazione:', error);
            } finally {
                this.$set(comment, '_reactingInProgress', false);
            }
        },

        startEditComment(comment) {
            this.clearCommentSaveTimer();
            this.editingCommentId = comment.id;
            this.editingCommentText = comment.comment || '';
            this.editingCommentLastSaved = this.editingCommentText;
            var raw = comment.mentioned_users;
            this.editingCommentBaselineMentions = Array.isArray(raw)
                ? raw.map(function(id) { return parseInt(id, 10); }).filter(function(id) { return !isNaN(id) && id > 0; })
                : [];
            this.editingMentionedUsers = this.editingCommentBaselineMentions.slice();
        },

        cancelEditComment() {
            this.clearCommentSaveTimer();
            this.editingCommentId = null;
            this.editingCommentText = '';
            this.editingCommentLastSaved = '';
            this.editingCommentBaselineMentions = [];
            this.editingMentionedUsers = [];
        },

        clearCommentSaveTimer() {
            if (this.commentSaveTimer) {
                clearTimeout(this.commentSaveTimer);
                this.commentSaveTimer = null;
            }
            this.commentSaveQueued = false;
        },

        debounceSaveEditComment() {
            if (!this.editingCommentId || this.isEditCommentEmpty) {
                return;
            }

            if (this.commentSaveTimer) {
                clearTimeout(this.commentSaveTimer);
            }

            this.commentSaveTimer = setTimeout(() => {
                this.commentSaveTimer = null;
                if (this.editingCommentId) {
                    this.saveEditComment(this.editingCommentId, { keepEditing: true });
                }
            }, 1500);
        },

        async saveEditComment(commentId, options) {
            const keepEditing = !!(options && options.keepEditing);

            if (this.commentSaveTimer) {
                clearTimeout(this.commentSaveTimer);
                this.commentSaveTimer = null;
            }

            const editEditor = this.editorByRef('editCommentEditor');
            if (editEditor && editEditor.uploadProgress && editEditor.uploadProgress.active) {
                this.commentSaveTimer = setTimeout(() => {
                    this.commentSaveTimer = null;
                    this.saveEditComment(commentId, options);
                }, 300);
                return;
            }

            if (this.isEditCommentEmpty) {
                return;
            }

            const payload = this.editingCommentText || '';
            if (keepEditing && payload === this.editingCommentLastSaved) {
                return;
            }

            if (this.isSavingComment) {
                if (keepEditing) {
                    this.commentSaveQueued = true;
                }
                return;
            }

            this.isSavingComment = true;
            try {
                const body = {
                    comment: payload,
                    format: 'html',
                };
                if (!keepEditing) {
                    var mentionEditor = this.editorByRef('editCommentEditor');
                    body.mentioned_users = mentionEditor && mentionEditor.getMentionedUserIds
                        ? mentionEditor.getMentionedUserIds()
                        : this.editingMentionedUsers;
                }
                const response = await axios.put(`/tasks/comments/${commentId}`, body);

                if (response.data.success) {
                    const index = this.comments.findIndex(c => c.id === commentId);
                    if (index !== -1) {
                        this.comments[index] = response.data.comment;
                    }
                    this.mergeImportedAttachments(response.data.imported_attachments);
                    this.editingCommentLastSaved = payload;

                    if (!keepEditing) {
                        this.editingCommentId = null;
                        this.editingCommentText = '';
                        this.editingCommentLastSaved = '';
                        this.editingCommentBaselineMentions = [];
                        this.editingMentionedUsers = [];
                    } else if (response.data.comment && Array.isArray(response.data.comment.mentioned_users)) {
                        this.editingCommentBaselineMentions = response.data.comment.mentioned_users
                            .map(function(id) { return parseInt(id, 10); })
                            .filter(function(id) { return !isNaN(id) && id > 0; });
                    }
                }
            } catch (error) {
                console.error('Errore modifica commento:', error);
                this.showError('Errore nella modifica del commento');
            } finally {
                this.isSavingComment = false;
                if (this.commentSaveQueued && this.editingCommentId === commentId) {
                    this.commentSaveQueued = false;
                    if ((this.editingCommentText || '') !== this.editingCommentLastSaved) {
                        await this.saveEditComment(commentId, { keepEditing: true });
                    }
                }
            }
        },

        connectToChannel() {
            var taskId = Number(this.subprojectId);
            if (!window.Echo || !taskId) {
                return;
            }
            if (Number(this.subscribedTaskChannelId) === taskId && this.echo) {
                return;
            }

            this.disconnectFromChannel();
            this.subscribedTaskChannelId = taskId;
            var self = this;
            this.echo = window.Echo.private('task.' + taskId)
                .listen('.comment.posted', function(e) {
                    var eventTaskId = Number(e.subproject_id || (e.comment && e.comment.subproject_id) || 0);
                    if (eventTaskId && eventTaskId !== Number(self.subprojectId)) {
                        return;
                    }
                    if (!e.comment || self.comments.some(function(comment) { return comment.id === e.comment.id; })) {
                        return;
                    }
                    self.comments.push(e.comment);
                    self.$nextTick(function() {
                        self.scrollToBottom();
                    });
                });
        },

        disconnectFromChannel() {
            var channelTaskId = this.subscribedTaskChannelId;
            if (this.echo && window.Echo && channelTaskId) {
                window.Echo.leave('task.' + channelTaskId);
            }
            this.echo = null;
            this.subscribedTaskChannelId = null;
        },

        scrollToBottom() {
            var self = this;
            
            window.setTimeout(function() {
                var commentsList = self.$refs.commentsList;
                if (commentsList && commentsList.scrollHeight > commentsList.clientHeight) {
                    commentsList.scrollTop = commentsList.scrollHeight;
                }
            }, 300);
            
            // Ripeti per catturare caricamento immagini
            window.setTimeout(function() {
                var commentsList = self.$refs.commentsList;
                if (commentsList) {
                    commentsList.scrollTop = commentsList.scrollHeight;
                }
            }, 1000);
        },
        
        loadDescriptionImagePreviews() {
            // Pulisci preview esistenti
            this.pastedDescriptionImages = [];
            
            if (!this.descriptionText) return;
            
            // Trova tutti i marker [img:ID] nella descrizione
            var regex = /\[img:(\d+)\]/g;
            var match;
            var self = this;
            
            while ((match = regex.exec(this.descriptionText)) !== null) {
                var imgId = parseInt(match[1]);
                
                // Cerca l'allegato corrispondente
                var attachment = this.attachments.find(function(att) {
                    return att.id === imgId;
                });
                
                if (attachment && attachment.mime_type && attachment.mime_type.indexOf('image/') === 0) {
                    this.pastedDescriptionImages.push({
                        id: attachment.id,
                        name: attachment.file_name,
                        url: attachment.download_url
                    });
                }
            }

            // Auto-resize dopo caricamento
            this.$nextTick(function() {
                self.autoResizeDescription();
            });
        },
        
        autoResizeDescription() {
            var textarea = this.$refs.descriptionTextarea;
            if (textarea) {
                var maxHeight = 300;
                textarea.style.height = 'auto';
                textarea.style.overflow = 'hidden';
                var newHeight = Math.max(32, Math.min(textarea.scrollHeight, maxHeight));
                textarea.style.height = newHeight + 'px';
                if (textarea.scrollHeight > maxHeight) {
                    textarea.style.overflow = 'auto';
                }
            }
        },
        
        openImageModal(img) {
            this.modalImageUrl = img.url;
            this.modalImageName = img.name || '';
            this.showImageModal = true;
        },
        
        handleCommentImageClick(event) {
            var target = event.target;
            if (target.tagName === 'IMG' && target.classList.contains('comment-inline-image')) {
                this.modalImageUrl = target.src;
                this.modalImageName = target.alt || '';
                this.showImageModal = true;
            }
        },

        openImagePreview(img) {
            this.modalImageUrl = img.url;
            this.modalImageName = img.name || '';
            this.showImageModal = true;
        },
        
        closeImageModal() {
            this.showImageModal = false;
            this.modalImageUrl = '';
            this.modalImageName = '';
        },
        
        async downloadModalImage() {
            if (!this.modalImageUrl) return;
            
            try {
                const response = await fetch(this.modalImageUrl);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = this.modalImageName || 'image';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Errore durante il download dell\'immagine:', error);
                // Fallback: apri l'immagine in una nuova tab
                window.open(this.modalImageUrl, '_blank');
            }
        },
        
        removeDescriptionImage(img) {
            // Rimuovi dalla lista preview
            var index = this.pastedDescriptionImages.findIndex(function(i) {
                return i.id === img.id;
            });
            if (index > -1) {
                this.pastedDescriptionImages.splice(index, 1);
            }
            
            // Salva automaticamente (il marker verrà omesso)
            this.saveDescription();
        },

        statusLabel(status) {
            if (window.__taskStatusLabels) {
                return window.__taskStatusLabels[status] || status;
            }
            return status;
        },

        statusBadgeStyle(status) {
            if (window.__taskStatusOptions) {
                const ts = window.__taskStatusOptions.find(s => s.slug === status);
                if (ts) {
                    return { backgroundColor: ts.color, color: '#fff' };
                }
            }
            return { backgroundColor: '#6b7280', color: '#fff' };
        },

        async saveTaskStatusFromSelect(newStatus) {
            if (!newStatus || newStatus === this.taskData.status) {
                return;
            }
            this.editingFields.status = newStatus;
            await this.saveFieldWithResponse('status');
        },

        taskSheetLabel(sheetId) {
            if (!sheetId) {
                return 'Nessun foglio';
            }
            const sheet = (this.taskData.task_sheets || []).find(s => s.id === sheetId);
            return sheet ? sheet.name : 'Foglio #' + sheetId;
        },

        async saveTaskSheet() {
            await this.saveField('task_sheet_id');
            this.taskData.task_sheet_id = this.editingFields.task_sheet_id;
        },

        priorityLabel(priority) {
            const labels = {
                'high': 'Alta',
                'medium': 'Media',
                'low': 'Bassa'
            };
            return labels[priority] || priority;
        },

        showSuccess(message) {
            if (typeof Swal !== 'undefined') {
                Swal.fire({
                    title: 'Successo',
                    text: message,
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else if (typeof toastr !== 'undefined') {
                toastr.success(message);
            } else {
                window.Swal.fire({ icon: 'success', title: 'Successo', text: message });
            }
        },
        
        showError(message) {
            if (typeof Swal !== 'undefined') {
                Swal.fire({
                    title: 'Errore',
                    text: message,
                    icon: 'error',
                    confirmButtonText: 'Ok'
                });
            } else if (typeof toastr !== 'undefined') {
                toastr.error(message);
            }
        },
        
        formatWeekDisplay(date) {
            // Converte la data da formato YYYY-MM-DD a DD/MM/YYYY
            if (!date) return '';
            const parts = date.split('-');
            if (parts.length === 3) {
                return `${parts[2]}/${parts[1]}/${parts[0]}`;
            }
            return date;
        },

        async preloadTimesheetData() {
            // Carica i timesheet in background senza cambiare tab
            // Usato per aggiornare il conteggio nel titolo della tab
            
            // Se già caricati, non ricaricare
            if (this.timesheetEntries.length > 0) {
                return;
            }
            
            // Non fare la chiamata se non abbiamo né subprojectId né projectId valido
            if (!this.subprojectId && !this.projectId) {
                console.log('📊 Nessun ID progetto/task disponibile per caricare i timesheet');
                return;
            }
            
            this.isLoadingTimesheet = true;
            
            try {
                // Se c'è subproject_id, carica i timesheet del task
                // Altrimenti carica tutti i timesheet del progetto
                let url = this.subprojectId 
                    ? `/tasks/${this.subprojectId}/timesheet`
                    : `/projects/${this.projectId}/timesheet`;
                
                // Costruisci parametri query
                const params = new URLSearchParams();
                
                // Aggiungi filtri per settimana se presenti
                if (this.weekStart && this.weekEnd) {
                    params.append('week_start', this.weekStart);
                    params.append('week_end', this.weekEnd);
                }
                
                // Aggiungi filtro utente se veniamo dalla pianificazione
                if (this.assignmentData && this.assignmentData.user && this.assignmentData.user.id) {
                    params.append('user_id', this.assignmentData.user.id);
                }
                
                if (params.toString()) {
                    url += '?' + params.toString();
                }
                
                const response = await axios.get(url);
                
                if (response.data.success) {
                    this.timesheetEntries = response.data.timesheets || [];
                    console.log('📊 Timesheet precaricati in background:', this.timesheetEntries.length);
                }
            } catch (error) {
                console.error('Errore caricamento timesheet in background:', error);
            } finally {
                this.isLoadingTimesheet = false;
            }
        },

        async loadTimesheetData() {
            // Cambia tab
            this.activeTab = 'timesheet';
            
            // Se già caricati per la stessa settimana, non ricaricare
            if (this.timesheetEntries.length > 0) {
                return;
            }
            
            // Non fare la chiamata se non abbiamo né subprojectId né projectId valido
            if (!this.subprojectId && !this.projectId) {
                console.log('📊 Nessun ID progetto/task disponibile per caricare i timesheet');
                this.showError('Nessun progetto associato per visualizzare i timesheet');
                return;
            }
            
            this.isLoadingTimesheet = true;
            
            try {
                // Se c'è subproject_id, carica i timesheet del task
                // Altrimenti carica tutti i timesheet del progetto
                let url = this.subprojectId 
                    ? `/tasks/${this.subprojectId}/timesheet`
                    : `/projects/${this.projectId}/timesheet`;
                
                // Costruisci parametri query
                const params = new URLSearchParams();
                
                // Aggiungi filtri per settimana se presenti
                if (this.weekStart && this.weekEnd) {
                    params.append('week_start', this.weekStart);
                    params.append('week_end', this.weekEnd);
                    console.log('📅 Caricamento timesheet per settimana:', this.weekStart, '-', this.weekEnd);
                }
                
                // Aggiungi filtro utente se veniamo dalla pianificazione
                if (this.assignmentData && this.assignmentData.user && this.assignmentData.user.id) {
                    params.append('user_id', this.assignmentData.user.id);
                    console.log('👤 Filtro timesheet per utente:', this.assignmentData.user.full_name);
                }
                
                // Aggiungi parametri all'URL se presenti
                if (params.toString()) {
                    url += `?${params.toString()}`;
                }
                    
                const response = await axios.get(url);
                if (response.data.success) {
                    this.timesheetEntries = response.data.entries;
                }
            } catch (error) {
                console.error('Errore caricamento timesheet:', error);
                this.showError('Errore nel caricamento dei timesheet');
            } finally {
                this.isLoadingTimesheet = false;
            }
        },

        // ========== INLINE EDITING METHODS ==========

        handleTitleInput(event) {
            const newValue = event.target.innerText;
            this.editingFields.name = newValue;
            this.debounceSave('name');
        },

        saveTitleField() {
            if (this.editingFields.name !== this.taskData.name) {
                this.saveField('name');
            }
        },

        handleFieldInput(field, event) {
            let newValue = event.target.innerText.trim();
            
            // Rimuovi "h" se presente per ore previste
            if (field === 'expected_duration') {
                newValue = newValue.replace(/h$/i, '').trim();
                // Converti in numero
                newValue = parseFloat(newValue) || null;
            }
            
            this.editingFields[field] = newValue;
            this.debounceSave(field);
        },

        debounceSave(field) {
            if (this.fieldSaveTimers[field]) {
                clearTimeout(this.fieldSaveTimers[field]);
            }

            this.fieldSaveTimers[field] = setTimeout(async () => {
                var response = await this.saveFieldWithResponse(field);
                if (field === 'expected_duration' && response && response.suggested_end_date) {
                    this.taskData.suggested_end_date = response.suggested_end_date;
                }
            }, 2000);
        },

        async saveField(field) {
            return await this.saveFieldWithResponse(field);
        },

        async saveFieldWithResponse(field) {
            if (this.fieldSaveTimers[field]) {
                clearTimeout(this.fieldSaveTimers[field]);
                delete this.fieldSaveTimers[field];
            }

            if (!this.subprojectId) return null;

            if (field === 'status') {
                if (!this.canModify && !this.canModifyTaskStatus) {
                    return null;
                }
            } else if (!this.canModify) {
                return null;
            }

            const value = this.editingFields[field];

            if (value === this.taskData[field]) return null;

            try {
                const response = await axios.patch(`/tasks/${this.subprojectId}/update-field`, {
                    field: field,
                    value: value
                });

                if (response.data.success) {
                    this.taskData[field] = value;
                    console.log(`✅ Campo ${field} salvato con successo`);

                    // Se la durata ha spostato la fine, aggiorna anche end_date
                    if (response.data.end_date_shifted && response.data.new_end_date) {
                        this.taskData.end_date = response.data.new_end_date;
                        this.editingFields.end_date = response.data.new_end_date;
                        this.$root.$emit('task-updated', {
                            taskId: this.subprojectId,
                            field: 'end_date',
                            value: response.data.new_end_date
                        });
                    }

                    const eventData = {
                        taskId: this.subprojectId,
                        field: field,
                        value: value,
                        updatedParents: response.data.updated_parents || null
                    };
                    this.$emit('task-updated', eventData);
                    if (this.$root) {
                        this.$root.$emit('task-updated', eventData);
                    }
                    return response.data;
                }
                return null;
            } catch (error) {
                var errMsg = 'Errore nel salvataggio';
                if (error.response && error.response.data && error.response.data.message) {
                    errMsg = error.response.data.message;
                }
                console.error(`Errore salvando campo ${field}:`, errMsg);
                this.showError(errMsg);
                this.editingFields[field] = this.taskData[field];
                return null;
            }
        },

        startEditingStartDate() {
            this.isEditingStartDate = true;
            this.$nextTick(() => {
                if (this.$refs.startDateInput) {
                    this.$refs.startDateInput.focus();
                }
            });
        },

        async saveStartDate() {
            const response = await this.saveFieldWithResponse('start_date');
            this.isEditingStartDate = false;
            if (response && response.suggested_end_date && !this.taskData.end_date) {
                this.taskData.suggested_end_date = response.suggested_end_date;
            }
        },

        startEditingEndDate() {
            this.isEditingEndDate = true;
            if (!this.editingFields.end_date && this.taskData.suggested_end_date) {
                this.editingFields.end_date = this.taskData.suggested_end_date;
            }
            this.$nextTick(() => {
                if (this.$refs.endDateInput) {
                    this.$refs.endDateInput.focus();
                }
            });
        },

        async saveEndDate() {
            await this.saveField('end_date');
            this.isEditingEndDate = false;
        },

        startEditingDueDate() {
            this.isEditingDueDate = true;
            this.$nextTick(() => {
                if (this.$refs.dueDateInput) {
                    this.$refs.dueDateInput.focus();
                }
            });
        },

        async saveDueDate() {
            await this.saveField('due_date');
            this.isEditingDueDate = false;
        },

        searchDependencyTasks() {
            clearTimeout(this.depSearchTimer);
            if (!this.depSearchQuery || this.depSearchQuery.length < 2) {
                this.depSearchResults = [];
                return;
            }
            this.depSearchTimer = setTimeout(() => {
                var self = this;
                $.ajax({
                    url: '/tasks/' + this.subprojectId + '/search-dependencies',
                    method: 'GET',
                    data: { q: this.depSearchQuery },
                    success: function(response) {
                        if (!response.success) return;
                        var existingPredIds = (self.taskData.predecessors || []).map(function(p) { return String(p.id); });
                        self.depSearchResults = response.tasks.filter(function(t) {
                            return existingPredIds.indexOf(String(t.id)) === -1;
                        });
                    }
                });
            }, 300);
        },

        closeDepSearch() {
            setTimeout(() => {
                this.isAddingDep = false;
                this.depSearchQuery = '';
                this.depSearchResults = [];
            }, 200);
        },

        async addDependency(predecessorId) {
            var self = this;
            try {
                await $.ajax({
                    url: '/subprojects/' + this.subprojectId + '/dependencies',
                    method: 'POST',
                    data: {
                        predecessor_id: predecessorId,
                        _token: document.querySelector('meta[name="csrf-token"]').content
                    }
                });
                this.isAddingDep = false;
                this.depSearchQuery = '';
                this.depSearchResults = [];
                await this.loadTaskDetails();
                this.$root.$emit('task-updated', {
                    taskId: this.subprojectId,
                    field: 'dependencies',
                    value: null
                });
            } catch (err) {
                var msg = (err.responseJSON && err.responseJSON.message) || 'Errore nella creazione della dipendenza';
                window.Swal.fire({ icon: 'error', title: 'Errore', text: msg });
            }
        },

        async removeDependency(pivotId) {
            try {
                await $.ajax({
                    url: '/subprojects/dependencies/' + pivotId,
                    method: 'DELETE',
                    data: {
                        _token: document.querySelector('meta[name="csrf-token"]').content
                    }
                });
                await this.loadTaskDetails();
                this.$root.$emit('task-updated', {
                    taskId: this.subprojectId,
                    field: 'dependencies',
                    value: null
                });
            } catch (err) {
                window.Swal.fire({ icon: 'error', title: 'Errore', text: 'Errore nella rimozione della dipendenza' });
            }
        },

        async savePriority() {
            await this.saveField('priority');
            this.taskData.priority = this.editingFields.priority;
        },

        openPrioritySelector(event) {
            console.log('🔵 openPrioritySelector chiamato', event);
            
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }
            
            // Chiudi dropdown tipo se aperto
            this.closeTypeSelector();
            
            // Usa currentTarget invece di target per ottenere l'elemento che ha il listener
            const element = event.currentTarget || event.target;
            const rect = element.getBoundingClientRect();
            
            console.log('📍 Posizione dropdown priorità:', { rect, top: rect.bottom + 5, left: rect.left });
            
            // Crea dropdown nel body
            const dropdown = document.createElement('div');
            dropdown.id = 'priority-dropdown-portal';
            dropdown.className = 'inline-dropdown';
            dropdown.style.cssText = `
                position: fixed;
                top: ${rect.bottom + 5}px;
                left: ${rect.left}px;
                z-index: 99999;
                background: #2a2a2a;
                border: 1px solid #444;
                border-radius: 6px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                min-width: 150px;
                padding: 4px 0;
                color: #fff;
            `;
            dropdown.innerHTML = `
                <div class="dropdown-item" data-priority="high" style="padding: 8px 12px; cursor: pointer;">
                    <span class="badge badge-priority-high">Alta</span>
                </div>
                <div class="dropdown-item" data-priority="medium" style="padding: 8px 12px; cursor: pointer;">
                    <span class="badge badge-priority-medium">Media</span>
                </div>
                <div class="dropdown-item" data-priority="low" style="padding: 8px 12px; cursor: pointer;">
                    <span class="badge badge-priority-low">Bassa</span>
                </div>
                <div class="dropdown-item" data-priority="" style="padding: 8px 12px; cursor: pointer;">
                    <span class="text-muted" style="color: #999;">Nessuna</span>
                </div>
            `;
            
            // Aggiungi event listeners
            dropdown.querySelectorAll('.dropdown-item').forEach(item => {
                item.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const priority = item.getAttribute('data-priority') || null;
                    this.selectPriority(priority);
                });
                // Hover effect
                item.addEventListener('mouseenter', () => {
                    item.style.backgroundColor = '#3a3a3a';
                });
                item.addEventListener('mouseleave', () => {
                    item.style.backgroundColor = 'transparent';
                });
            });
            
            // Previeni chiusura quando clicchi sul dropdown
            dropdown.addEventListener('click', (e) => e.stopPropagation());
            
            document.body.appendChild(dropdown);
            this.showPrioritySelector = true;
            
            console.log('✅ Dropdown priorità creato nel body');
            
            // Chiudi quando si clicca fuori
            this.$nextTick(() => {
                setTimeout(() => {
                    document.addEventListener('click', this.closePrioritySelector);
                }, 10);
            });
        },

        closePrioritySelector() {
            this.showPrioritySelector = false;
            document.removeEventListener('click', this.closePrioritySelector);
            
            // Rimuovi dropdown dal body
            const dropdown = document.getElementById('priority-dropdown-portal');
            if (dropdown) {
                dropdown.remove();
            }
        },

        selectPriority(priority) {
            this.editingFields.priority = priority;
            this.saveField('priority');
            this.closePrioritySelector();
        },

        openTypeSelector(event) {
            console.log('🔵 openTypeSelector chiamato', event);
            
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }
            
            // Chiudi dropdown priorità se aperto
            this.closePrioritySelector();
            
            // Usa currentTarget invece di target per ottenere l'elemento che ha il listener
            const element = event.currentTarget || event.target;
            const rect = element.getBoundingClientRect();
            
            console.log('📍 Posizione dropdown tipo:', { rect, top: rect.bottom + 5, left: rect.left });
            
            // Crea dropdown nel body
            const dropdown = document.createElement('div');
            dropdown.id = 'type-dropdown-portal';
            dropdown.className = 'inline-dropdown';
            dropdown.style.cssText = `
                position: fixed;
                top: ${rect.bottom + 5}px;
                left: ${rect.left}px;
                z-index: 99999;
                background: #2a2a2a;
                border: 1px solid #444;
                border-radius: 6px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                min-width: 150px;
                padding: 4px 0;
                color: #fff;
            `;
            dropdown.innerHTML = `
                <div class="dropdown-item" data-type="Fisso" style="padding: 8px 12px; cursor: pointer; color: #ddd;">Fisso</div>
                <div class="dropdown-item" data-type="T&M" style="padding: 8px 12px; cursor: pointer; color: #ddd;">T&M</div>
                <div class="dropdown-item" data-type="Misto" style="padding: 8px 12px; cursor: pointer; color: #ddd;">Misto</div>
                <div class="dropdown-item" data-type="" style="padding: 8px 12px; cursor: pointer;">
                    <span class="text-muted" style="color: #999;">Nessuno</span>
                </div>
            `;
            
            // Aggiungi event listeners
            dropdown.querySelectorAll('.dropdown-item').forEach(item => {
                item.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const type = item.getAttribute('data-type') || null;
                    this.selectType(type);
                });
                // Hover effect
                item.addEventListener('mouseenter', () => {
                    item.style.backgroundColor = '#3a3a3a';
                });
                item.addEventListener('mouseleave', () => {
                    item.style.backgroundColor = 'transparent';
                });
            });
            
            // Previeni chiusura quando clicchi sul dropdown
            dropdown.addEventListener('click', (e) => e.stopPropagation());
            
            document.body.appendChild(dropdown);
            this.showTypeSelector = true;
            
            console.log('✅ Dropdown tipo creato nel body');
            
            this.$nextTick(() => {
                setTimeout(() => {
                    document.addEventListener('click', this.closeTypeSelector);
                }, 10);
            });
        },

        closeTypeSelector() {
            this.showTypeSelector = false;
            document.removeEventListener('click', this.closeTypeSelector);
            
            // Rimuovi dropdown dal body
            const dropdown = document.getElementById('type-dropdown-portal');
            if (dropdown) {
                dropdown.remove();
            }
        },

        selectType(type) {
            this.editingFields.commercial_agreement = type;
            this.saveField('commercial_agreement');
            this.closeTypeSelector();
        },

        openAssigneeSelector() {
            this.showAssigneeSelector = true;
            this.assigneeSearchQuery = '';
            
            this.$nextTick(() => {
                if (this.$refs.assigneeSearch) {
                    this.$refs.assigneeSearch.focus();
                }
            });
        },

        closeAssigneeSelector() {
            this.showAssigneeSelector = false;
            this.assigneeSearchQuery = '';
        },

        toggleAssignee(user) {
            const isAssigned = this.isUserAssigned(user.id);
            
            if (isAssigned) {
                this.removeAssignee(user.id);
            } else {
                this.addAssignee(user);
            }
        },

        async addAssignee(user) {
            try {
                const response = await axios.post(`/tasks/${this.subprojectId}/assign-user`, {
                    user_id: user.id
                });

                if (response.data.success) {
                    this.taskData.assigned_users.push({
                        id: user.id,
                        full_name: user.full_name,
                        avatar: user.avatar,
                        initials: user.initials
                    });
                    console.log(`✅ Utente ${user.full_name} assegnato`);
                    // Emetti evento per aggiornare la griglia (sia local che root)
                    const eventData = {
                        taskId: this.subprojectId,
                        field: 'assigned_users',
                        action: 'add',
                        user: user
                    };
                    this.$emit('task-updated', eventData);
                    // Emetti anche sull'istanza root per intercettazione globale
                    if (this.$root) {
                        this.$root.$emit('task-updated', eventData);
                    }
                }
            } catch (error) {
                console.error('Errore assegnando utente:', error);
                this.showError('Errore nell\'assegnazione dell\'utente');
            }
        },

        async removeAssignee(userId) {
            try {
                const response = await axios.post(`/tasks/${this.subprojectId}/unassign-user`, {
                    user_id: userId
                });

                if (response.data.success) {
                    this.taskData.assigned_users = this.taskData.assigned_users.filter(u => u.id !== userId);
                    console.log(`✅ Utente rimosso`);
                    // Emetti evento per aggiornare la griglia (sia local che root)
                    const eventData = {
                        taskId: this.subprojectId,
                        field: 'assigned_users',
                        action: 'remove',
                        userId: userId
                    };
                    this.$emit('task-updated', eventData);
                    // Emetti anche sull'istanza root per intercettazione globale
                    if (this.$root) {
                        this.$root.$emit('task-updated', eventData);
                    }
                }
            } catch (error) {
                console.error('Errore rimuovendo utente:', error);
                this.showError('Errore nella rimozione dell\'utente');
            }
        },

        isUserAssigned(userId) {
            return this.taskData.assigned_users.some(u => u.id === userId);
        },

        // ===== METODI PER MODIFICA ASSEGNAZIONE =====
        
        async loadAvailableProjects() {
            if (this.availableProjects.length > 0) {
                return; // Già caricati
            }
            
            this.isLoadingProjects = true;
            try {
                const response = await axios.get('/projects/active');
                if (response.data.success) {
                    this.availableProjects = response.data.projects;
                    console.log('📁 Progetti attivi caricati:', this.availableProjects.length);
                }
            } catch (error) {
                console.error('Errore caricamento progetti:', error);
                this.showError('Errore nel caricamento dei progetti');
            } finally {
                this.isLoadingProjects = false;
            }
        },

        async loadAvailableSubprojects(projectId) {
            this.isLoadingSubprojects = true;
            this.availableSubprojects = [];
            try {
                const response = await axios.get(`/admin/developers-planning/subprojects?project_id=${projectId}`);
                if (response.data.success) {
                    // Il backend già restituisce la struttura gerarchica con indent_level
                    this.availableSubprojects = response.data.subprojects.map(sp => ({
                        id: sp.id,
                        name: sp.name, // Già formattato con indentazione e prefisso
                        raw_name: sp.raw_name || sp.name, // Nome senza indentazione
                        project_name: sp.project_name || '', // Nome progetto per ricerca
                        indent_level: sp.indent_level || 0,
                        has_children: sp.has_children || false
                    }));
                    console.log('📋 Task caricati:', this.availableSubprojects.length);
                    
                    // Applica il filtro per mostrare i task anche senza ricerca
                    this.filterSubprojects();
                }
            } catch (error) {
                console.error('Errore caricamento task:', error);
                this.showError('Errore nel caricamento dei task');
            } finally {
                this.isLoadingSubprojects = false;
            }
        },

        async loadAvailableUsers() {
            if (this.availableUsers.length > 0) {
                return; // Già caricati
            }
            
            try {
                const response = await axios.get('/users/all');
                if (response.data) {
                    this.availableUsers = response.data.map(user => ({
                        id: user.id,
                        full_name: user.name + ' ' + user.surname,
                        division: user.division
                    }));
                    console.log('👥 Utenti caricati:', this.availableUsers.length);
                }
            } catch (error) {
                console.error('Errore caricamento utenti:', error);
                this.showError('Errore nel caricamento degli utenti');
            }
        },

        filterProjects() {
            if (!this.projectSearchQuery || this.projectSearchQuery.length < 2) {
                this.filteredProjects = [];
                return;
            }
            
            if (this.availableProjects.length === 0) {
                this.loadAvailableProjects().then(() => {
                    this.performProjectFilter();
                });
            } else {
                this.performProjectFilter();
            }
        },

        performProjectFilter() {
            const query = this.projectSearchQuery.toLowerCase();
            this.filteredProjects = this.availableProjects.filter(project => 
                project.title.toLowerCase().includes(query) ||
                project.client_name.toLowerCase().includes(query)
            );
            this.selectedProjectIndex = -1;
        },

        onSubprojectInputFocus() {
            // Svuota il campo per mostrare tutti i task disponibili
            this.subprojectSearchQuery = '';
            
            // Mostra il dropdown
            this.showSubprojectDropdown = true;
            
            // Se non ci sono task caricati e c'è un progetto selezionato, caricali
            if (this.availableSubprojects.length === 0 && this.editingAssignment.newProjectId) {
                this.loadAvailableSubprojects(this.editingAssignment.newProjectId);
            } else {
                // Altrimenti filtra subito (mostrerà tutti i task)
                this.filterSubprojects();
            }
        },

        filterSubprojects() {
            // Se non c'è ricerca, mostra tutti i task
            if (!this.subprojectSearchQuery || this.subprojectSearchQuery.length === 0) {
                this.filteredSubprojects = this.availableSubprojects;
                this.selectedSubprojectIndex = -1;
                return;
            }
            
            // Se c'è una ricerca, cerca nel nome del task e nel nome del progetto
            const query = this.subprojectSearchQuery.toLowerCase();
            this.filteredSubprojects = this.availableSubprojects.filter(sp => {
                const taskNameMatch = sp.name.toLowerCase().includes(query);
                const rawNameMatch = sp.raw_name && sp.raw_name.toLowerCase().includes(query);
                const projectNameMatch = sp.project_name && sp.project_name.toLowerCase().includes(query);
                
                return taskNameMatch || rawNameMatch || projectNameMatch;
            });
            this.selectedSubprojectIndex = -1;
        },

        selectProject(project) {
            this.editingAssignment.newProjectId = project.id;
            this.editingAssignment.newProjectName = project.title;
            this.projectSearchQuery = `${project.title} (${project.client_name})`;
            this.showProjectDropdown = false;
            
            // Reset task selection
            this.clearSubproject();
            
            // Carica i task del progetto selezionato
            this.loadAvailableSubprojects(project.id);
            
            console.log('✅ Progetto selezionato:', project.title);
        },

        selectSubproject(subproject) {
            this.editingAssignment.newSubprojectId = subproject.id;
            this.editingAssignment.newSubprojectName = subproject.name.trim();
            this.subprojectSearchQuery = subproject.name.trim();
            this.showSubprojectDropdown = false;
            
            console.log('✅ Task selezionato:', subproject.name);
        },

        clearProject() {
            this.editingAssignment.newProjectId = null;
            this.editingAssignment.newProjectName = '';
            this.projectSearchQuery = '';
            this.clearSubproject();
        },

        clearSubproject() {
            this.editingAssignment.newSubprojectId = null;
            this.editingAssignment.newSubprojectName = '';
            this.subprojectSearchQuery = '';
            this.availableSubprojects = [];
            this.cancelCreatingSubtask();
        },
        
        // ===== CREAZIONE RAPIDA TASK =====
        
        async createQuickTask() {
            if (!this.editingAssignment.newProjectId) {
                this.showError('Seleziona prima un progetto');
                return;
            }
            
            const taskName = this.subprojectSearchQuery.trim();
            if (!taskName) {
                this.showError('Inserisci un nome per il task');
                return;
            }
            
            this.isCreatingTask = true;
            try {
                console.log('🔵 Creando task per progetto:', this.editingAssignment.newProjectId);
                
                // Prepara i dati per la creazione
                const taskData = {
                    name: taskName,
                    priority: 'medium',
                    status: 'todo',
                    commercial_agreement: 'consuntivo'
                };
                
                // Se c'è un utente selezionato, assegnagli il task
                if (this.editingAssignment.newUserId) {
                    taskData.assigned_users = [this.editingAssignment.newUserId];
                    console.log('🔵 Assegnando task all\'utente:', this.editingAssignment.newUserId);
                }
                
                const response = await axios.post(`/subprojects/grid/${this.editingAssignment.newProjectId}`, taskData);
                console.log('✅ Risposta creazione task:', response.data);
                
                if (response.data.success) {
                    const newTask = response.data.subproject;
                    console.log('✅ Task creato:', newTask);
                    
                    // Ricarica la lista dei task
                    await this.loadAvailableSubprojects(this.editingAssignment.newProjectId);
                    
                    // Seleziona immediatamente il task appena creato
                    this.selectSubproject({
                        id: newTask.id,
                        name: newTask.name,
                        indent_level: 0
                    });
                } else {
                    throw new Error(response.data.message || 'Errore nella creazione del task');
                }
            } catch (error) {
                console.error('Errore creazione task:', error);
                const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Errore nella creazione del task';
                this.showError(errorMessage);
            } finally {
                this.isCreatingTask = false;
            }
        },
        
        startCreatingSubtask(parentId) {
            // Trova il task padre per ottenere il suo indent_level
            const parentTask = this.availableSubprojects.find(sp => sp.id === parentId);
            if (!parentTask) {
                console.error('Task padre non trovato');
                return;
            }
            
            this.creatingSubtaskForParentId = parentId;
            this.newSubtaskName = '';
            
            // Focus sull'input dopo il prossimo render
            this.$nextTick(() => {
                setTimeout(() => {
                    const input = this.$el.querySelector('.subtask-create-input');
                    if (input) {
                        input.focus();
                        input.select();
                    }
                }, 100);
            });
        },
        
        cancelCreatingSubtask() {
            this.creatingSubtaskForParentId = null;
            this.newSubtaskName = '';
        },
        
        handleSubtaskInputBlur(event) {
            // Previeni la chiusura se l'utente sta cliccando dentro il dropdown
            const relatedTarget = event.relatedTarget;
            if (relatedTarget) {
                // Se il focus va a un elemento dentro il dropdown, non chiudere
                const dropdown = this.$el.querySelector('.subproject-autocomplete-dropdown');
                if (dropdown && dropdown.contains(relatedTarget)) {
                    return;
                }
                // Se il focus va all'input principale, non chiudere
                const mainInput = this.$el.querySelector('.autocomplete-input');
                if (mainInput && (mainInput === relatedTarget || mainInput.contains(relatedTarget))) {
                    return;
                }
            }
            
            // Ritarda la chiusura per permettere il click su altri elementi
            setTimeout(() => {
                // Verifica che l'input non sia ancora in focus
                const input = this.$el.querySelector('.subtask-create-input');
                if (input && document.activeElement === input) {
                    return; // L'input è ancora in focus, non chiudere
                }
                
                // Se l'input principale è in focus, non chiudere
                const mainInput = this.$el.querySelector('.autocomplete-input');
                if (mainInput && document.activeElement === mainInput) {
                    return; // L'input principale è in focus, non chiudere
                }
                
                if (this.creatingSubtaskForParentId && !this.newSubtaskName.trim()) {
                    this.cancelCreatingSubtask();
                }
            }, 300);
        },
        
        async createQuickSubtask(parentId) {
            const taskName = this.newSubtaskName.trim();
            if (!taskName) {
                this.showError('Inserisci un nome per il sottotask');
                return;
            }
            
            if (!this.editingAssignment.newProjectId) {
                this.showError('Seleziona prima un progetto');
                this.cancelCreatingSubtask();
                return;
            }
            
            this.isCreatingSubtask = true;
            try {
                console.log('🔵 Creando sottotask per parent:', parentId, 'nome:', taskName);
                
                // Prepara i dati per la creazione
                const subtaskData = {
                    name: taskName
                };
                
                // Se c'è un utente selezionato, assegnagli il sottotask
                if (this.editingAssignment.newUserId) {
                    subtaskData.assigned_users = [this.editingAssignment.newUserId];
                    console.log('🔵 Assegnando sottotask all\'utente:', this.editingAssignment.newUserId);
                }
                
                const response = await axios.post(`/subprojects/grid/create-subtask/${parentId}`, subtaskData);
                console.log('✅ Risposta creazione sottotask:', response.data);
                
                if (response.data.success) {
                    const newSubtask = response.data.subtask;
                    console.log('✅ Subtask creato:', newSubtask);
                    
                    // Chiudi l'input inline
                    this.cancelCreatingSubtask();
                    
                    // Ricarica la lista dei task
                    await this.loadAvailableSubprojects(this.editingAssignment.newProjectId);
                    
                    // Seleziona immediatamente il sottotask appena creato
                    this.selectSubproject({
                        id: newSubtask.id,
                        name: newSubtask.name,
                        indent_level: (this.getSubtaskParentIndentLevel() || 0) + 1
                    });
                } else {
                    throw new Error(response.data.message || 'Errore nella creazione del sottotask');
                }
            } catch (error) {
                console.error('Errore creazione sottotask:', error);
                const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Errore nella creazione del sottotask';
                this.showError(errorMessage);
            } finally {
                this.isCreatingSubtask = false;
            }
        },
        
        getSubtaskParentIndentLevel() {
            if (!this.creatingSubtaskForParentId) return 0;
            const parentTask = this.availableSubprojects.find(sp => sp.id === this.creatingSubtaskForParentId);
            return parentTask ? (parentTask.indent_level || 0) : 0;
        },
        
        isExactMatch(query) {
            if (!query) return false;
            const lowerQuery = query.toLowerCase().trim();
            return this.filteredSubprojects.some(sp => 
                sp.name.toLowerCase().trim() === lowerQuery || 
                (sp.raw_name && sp.raw_name.toLowerCase().trim() === lowerQuery)
            );
        },
        
        canCreateNewTask() {
            return this.subprojectSearchQuery && 
                   this.subprojectSearchQuery.length > 0 && 
                   this.editingAssignment.newProjectId &&
                   !this.isCreatingTask;
        },
        
        getMaxSubprojectIndex() {
            return this.filteredSubprojects.length - 1;
        },

        hideProjectDropdownDelayed() {
            setTimeout(() => {
                this.showProjectDropdown = false;
            }, 200);
        },

        hideSubprojectDropdownDelayed() {
            // Non chiudere se stiamo creando un sottotask
            if (this.creatingSubtaskForParentId) {
                return;
            }
            
            setTimeout(() => {
                // Verifica di nuovo prima di chiudere (potrebbe essere cambiato nel frattempo)
                if (!this.creatingSubtaskForParentId) {
                    this.showSubprojectDropdown = false;
                }
            }, 200);
        },
        
        handleMainSubprojectInputBlur(event) {
            // Se il focus va all'input del sottotask, non chiudere il dropdown
            const relatedTarget = event.relatedTarget;
            if (relatedTarget) {
                const subtaskInput = this.$el.querySelector('.subtask-create-input');
                if (subtaskInput && (subtaskInput === relatedTarget || subtaskInput.contains(relatedTarget))) {
                    // Il focus va all'input del sottotask, non chiudere
                    return;
                }
                // Se il focus va a un elemento dentro il dropdown, non chiudere
                const dropdown = this.$el.querySelector('.subproject-autocomplete-dropdown');
                if (dropdown && dropdown.contains(relatedTarget)) {
                    return;
                }
            }
            
            // Altrimenti, chiudi normalmente
            this.hideSubprojectDropdownDelayed();
        },

        filterUsers() {
            // Se non c'è ricerca, mostra tutti gli utenti
            if (!this.userSearchQuery || this.userSearchQuery.length === 0) {
                this.filteredUsers = this.availableUsers;
                this.selectedUserIndex = -1;
                return;
            }
            
            // Se c'è una ricerca, cerca nel nome completo e divisione
            const query = this.userSearchQuery.toLowerCase();
            this.filteredUsers = this.availableUsers.filter(user => {
                const fullNameMatch = user.full_name.toLowerCase().includes(query);
                const divisionMatch = user.division && user.division.toLowerCase().includes(query);
                
                return fullNameMatch || divisionMatch;
            });
            this.selectedUserIndex = -1;
        },

        onUserInputFocus() {
            // Svuota il campo per mostrare tutti gli utenti disponibili
            this.userSearchQuery = '';
            
            // Mostra il dropdown
            this.showUserDropdown = true;
            
            // Se non ci sono utenti caricati, caricali
            if (this.availableUsers.length === 0) {
                this.loadAvailableUsers();
            } else {
                // Altrimenti filtra subito (mostrerà tutti gli utenti)
                this.filterUsers();
            }
        },

        selectUser(user) {
            this.editingAssignment.newUserId = user.id;
            this.editingAssignment.newUserName = user.full_name;
            this.userSearchQuery = user.full_name + (user.division ? ` (${user.division})` : '');
            this.showUserDropdown = false;
            
            console.log('✅ Utente selezionato:', user.full_name);
        },

        
        formatDateForShow(date) {
            if (!date) return '';
            try {
                let formatDate;
                if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    const parts = date.split('-');
                    formatDate = new Date(parts[0], parts[1] - 1, parts[2]);
                } else {
                    formatDate = new Date(date);
                }
                if (isNaN(formatDate.getTime())) return '';
                const day = String(formatDate.getDate()).padStart(2, '0');
                const month = String(formatDate.getMonth() + 1).padStart(2, '0');
                const year = formatDate.getFullYear();
                return `${day}/${month}/${year}`;
            } catch (error) {
                return '';
            }
        },
        
        initializeTaskSidebarSpecificDatePicker() {
            if (!this.$refs.taskSidebarSpecificDateInput) return;
            const $input = $(this.$refs.taskSidebarSpecificDateInput);
            
            // Rimuovi datepicker esistente se presente
            if ($input.data('daterangepicker')) {
                $input.data('daterangepicker').remove();
            }
            
            // Nuova assegnazione: solo giorni nella settimana selezionata. In modifica: nessun vincolo (spostamento su settimane future/past).
            let minDate = false;
            let maxDate = false;
            if (
                this.isCreationMode &&
                this.assignmentData &&
                this.assignmentData.week_start &&
                this.assignmentData.week_end
            ) {
                minDate = moment(this.assignmentData.week_start, 'YYYY-MM-DD').toDate();
                maxDate = moment(this.assignmentData.week_end, 'YYYY-MM-DD').toDate();
            }
            
            // Imposta valore iniziale
            let initialValue = '';
            if (this.editingAssignment.specificDate) {
                initialValue = this.formatDateForShow(this.editingAssignment.specificDate);
            }
            $input.val(initialValue);
            
            // Inizializza datepicker
            const locale = $.extend(true, {}, daterangepickerLocale, { format: 'DD/MM/YYYY' });
            $input.daterangepicker({
                singleDatePicker: true,
                autoUpdateInput: false,
                showDropdowns: true,
                minDate: minDate,
                maxDate: maxDate,
                locale: locale,
                drops: 'up',
                opens: 'left',
                parentEl: this.$el.querySelector('.task-sidebar-content') || '.task-sidebar'
            });
            
            // Event listener per quando viene selezionata una data
            $input.on('apply.daterangepicker', (ev, picker) => {
                const formattedDate = picker.startDate.format('DD/MM/YYYY');
                $input.val(formattedDate);
                const dateParts = formattedDate.split('/');
                if (dateParts.length === 3) {
                    this.editingAssignment.specificDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
                }
            });
            
            // Event listener per quando viene cancellata la selezione
            $input.on('cancel.daterangepicker', () => {
                $input.val('');
                this.editingAssignment.specificDate = null;
            });
        },
        clearUser() {
            this.editingAssignment.newUserId = null;
            this.editingAssignment.newUserName = '';
            this.userSearchQuery = '';
            this.filteredUsers = [];
            console.log('🧹 Utente deselezionato');
        },

        hideUserDropdownDelayed() {
            setTimeout(() => {
                this.showUserDropdown = false;
            }, 200);
        },

        // ===== NAVIGAZIONE CON TASTIERA =====
        
        handleProjectKeydown(event) {
            if (!this.showProjectDropdown || this.filteredProjects.length === 0) return;
            
            switch(event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    this.selectedProjectIndex = Math.min(this.selectedProjectIndex + 1, this.filteredProjects.length - 1);
                    this.scrollIntoView('project', this.selectedProjectIndex);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.selectedProjectIndex = Math.max(this.selectedProjectIndex - 1, 0);
                    this.scrollIntoView('project', this.selectedProjectIndex);
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (this.selectedProjectIndex >= 0 && this.selectedProjectIndex < this.filteredProjects.length) {
                        this.selectProject(this.filteredProjects[this.selectedProjectIndex]);
                        this.selectedProjectIndex = -1;
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    this.showProjectDropdown = false;
                    this.selectedProjectIndex = -1;
                    break;
            }
        },
        
        handleSubprojectKeydown(event) {
            if (!this.showSubprojectDropdown) return;
            
            // Se stiamo creando un sottotask, gestisci l'input inline
            if (this.creatingSubtaskForParentId) {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    this.cancelCreatingSubtask();
                }
                return;
            }
            
            // Se non ci sono risultati ma c'è una ricerca, permettere creazione con Invio
            if (this.subprojectSearchQuery && this.subprojectSearchQuery.length > 0 && this.filteredSubprojects.length === 0) {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    this.createQuickTask();
                    return;
                }
            }
            
            if (this.filteredSubprojects.length === 0) return;
            
            switch(event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    // Se siamo in fondo e c'è opzione "Crea nuovo task", selezionala
                    const maxIndex = this.getMaxSubprojectIndex();
                    if (this.selectedSubprojectIndex >= maxIndex && this.canCreateNewTask()) {
                        this.selectedSubprojectIndex = maxIndex + 1; // Indice per "Crea nuovo task"
                    } else {
                        this.selectedSubprojectIndex = Math.min(this.selectedSubprojectIndex + 1, maxIndex);
                    }
                    this.scrollIntoView('subproject', this.selectedSubprojectIndex);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    // Se siamo sull'opzione "Crea nuovo task", vai all'ultimo task
                    if (this.selectedSubprojectIndex > this.filteredSubprojects.length - 1) {
                        this.selectedSubprojectIndex = this.filteredSubprojects.length - 1;
                    } else {
                        this.selectedSubprojectIndex = Math.max(this.selectedSubprojectIndex - 1, 0);
                    }
                    this.scrollIntoView('subproject', this.selectedSubprojectIndex);
                    break;
                case 'Enter':
                    event.preventDefault();
                    // Se è selezionata l'opzione "Crea nuovo task"
                    if (this.selectedSubprojectIndex > this.filteredSubprojects.length - 1 && this.canCreateNewTask()) {
                        this.createQuickTask();
                    } else if (this.selectedSubprojectIndex >= 0 && this.selectedSubprojectIndex < this.filteredSubprojects.length) {
                        this.selectSubproject(this.filteredSubprojects[this.selectedSubprojectIndex]);
                        this.selectedSubprojectIndex = -1;
                    } else if (this.subprojectSearchQuery && this.subprojectSearchQuery.length > 0) {
                        // Se c'è una ricerca ma nessuna selezione, crea comunque
                        this.createQuickTask();
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    this.showSubprojectDropdown = false;
                    this.selectedSubprojectIndex = -1;
                    break;
            }
        },
        
        handleUserKeydown(event) {
            if (!this.showUserDropdown || this.filteredUsers.length === 0) return;
            
            switch(event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    this.selectedUserIndex = Math.min(this.selectedUserIndex + 1, this.filteredUsers.length - 1);
                    this.scrollIntoView('user', this.selectedUserIndex);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.selectedUserIndex = Math.max(this.selectedUserIndex - 1, 0);
                    this.scrollIntoView('user', this.selectedUserIndex);
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (this.selectedUserIndex >= 0 && this.selectedUserIndex < this.filteredUsers.length) {
                        this.selectUser(this.filteredUsers[this.selectedUserIndex]);
                        this.selectedUserIndex = -1;
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    this.showUserDropdown = false;
                    this.selectedUserIndex = -1;
                    break;
            }
        },
        
        scrollIntoView(type, index) {
            this.$nextTick(() => {
                const selector = `.autocomplete-item-${type}-${index}`;
                const element = document.querySelector(selector);
                if (element) {
                    element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
            });
        },

        clearProject() {
            this.editingAssignment.newProjectId = null;
            this.editingAssignment.newProjectName = '';
            this.projectSearchQuery = '';
            this.filteredProjects = [];
            
            // Reset anche task e utente quando si cambia progetto
            this.clearSubproject();
            this.clearUser();
            
            console.log('🧹 Progetto deselezionato');
        },

        clearSubproject() {
            this.editingAssignment.newSubprojectId = null;
            this.editingAssignment.newSubprojectName = '';
            this.subprojectSearchQuery = '';
            this.filteredSubprojects = [];
            this.availableSubprojects = [];
            console.log('🧹 Task deselezionato');
        },

        async confirmAssignmentChange() {
            // Validazione
            if (!this.isAssignmentValid) {
                this.showError('Compila tutti i campi obbligatori');
                return;
            }

            // Modalità creazione vs modifica
            if (this.isCreationMode) {
                // Modalità creazione - messaggio più semplice
                const userName = this.editingAssignment.newUserName || (this.assignmentData && this.assignmentData.user ? this.assignmentData.user.full_name : 'Utente');
                const project = this.editingAssignment.newProjectName || 'Progetto';
                const task = this.editingAssignment.newSubprojectName || '(solo progetto)';

                const result = await Swal.fire({
                    title: 'Crea Nuova Assegnazione',
                    html: `
                        <div style="text-align: left;">
                            <p><strong>Utente:</strong> ${userName}</p>
                            <p><strong>Progetto:</strong> ${project} ${task !== '(solo progetto)' ? '> ' + task : ''}</p>
                            <p><strong>Ore pianificate:</strong> ${this.editingAssignment.newHours}h</p>
                        </div>
                    `,
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Crea',
                    cancelButtonText: 'Annulla',
                    confirmButtonColor: '#ff9800',
                    cancelButtonColor: '#666'
                });

                if (result.isConfirmed) {
                    await this.saveAssignmentChange();
                }
            } else {
                // Modalità modifica - messaggio completo
                if (!this.assignmentData || !this.assignmentData.user) {
                    this.showError('Dati assegnazione non disponibili per la modifica');
                    return;
                }
                
                const oldProject = this.taskData.name || 'Progetto corrente';
                const newProject = this.editingAssignment.newProjectName || 'Nuovo progetto';
                const newTask = this.editingAssignment.newSubprojectName || '(solo progetto)';
                
                // Controlla se l'utente è cambiato
                const oldUserName = this.assignmentData.user.full_name;
                let newUserName = null;
                if (this.editingAssignment.newUserId && this.editingAssignment.newUserId !== this.assignmentData.user.id) {
                    const newUser = this.availableUsers.find(u => u.id === this.editingAssignment.newUserId);
                    newUserName = newUser ? newUser.full_name : null;
                }

                const result = await Swal.fire({
                    title: 'Conferma Modifica',
                    html: `
                        <div style="text-align: left;">
                            <p><strong>Da:</strong> ${oldProject}</p>
                            <p><strong>A:</strong> ${newProject} ${newTask !== '(solo progetto)' ? '> ' + newTask : ''}</p>
                            ${newUserName ? `<p><strong>Nuovo utente:</strong> ${oldUserName} → ${newUserName}</p>` : ''}
                            <p style="color: #ff9800; margin-top: 15px;">
                                <i class="fa fa-warning"></i> Le ore pianificate verranno azzerate.
                            </p>
                            <p><strong>Nuove ore pianificate:</strong> ${this.editingAssignment.newHours}h</p>
                        </div>
                    `,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Conferma',
                    cancelButtonText: 'Annulla',
                    confirmButtonColor: '#ff9800',
                    cancelButtonColor: '#666'
                });

                if (result.isConfirmed) {
                    await this.saveAssignmentChange();
                }
            }
        },

        async saveAssignmentChange() {
            this.isSavingAssignment = true;
            try {
                let response;
                let successMessage;
                
                if (this.isCreationMode) {
                    // Modalità creazione - POST a /api/setdevelopersplanning
                    // Il metodo store richiede "date" invece di week_start/week_end
                    
                    // Determina la data da usare: priorità a specificDate, poi week_start, poi oggi
                    let dateToUse = null;
                    if (this.editingAssignment.specificDate) {
                        dateToUse = this.editingAssignment.specificDate;
                    } else if (this.assignmentData && this.assignmentData.week_start) {
                        dateToUse = this.assignmentData.week_start;
                    } else {
                        // Fallback: usa la data di oggi
                        const today = new Date();
                        dateToUse = today.getFullYear() + '-' + 
                            String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                            String(today.getDate()).padStart(2, '0');
                    }
                    
                    const payload = {
                        user_id: this.editingAssignment.newUserId,
                        project_id: this.editingAssignment.newProjectId,
                        subproject_id: this.editingAssignment.newSubprojectId || null,
                        hours: this.editingAssignment.newHours,
                        description: this.editingAssignment.newDescription || '',
                        date: dateToUse
                    };
                    
                    // Aggiungi specific_date se presente
                    if (this.editingAssignment.specificDate) {
                        payload.specific_date = this.editingAssignment.specificDate;
                    }
                    
                    response = await axios.post('/api/setdevelopersplanning', payload, {
                        headers: { Authorization: `Bearer ${this.apiToken}` }
                    });
                    successMessage = 'Assegnazione creata con successo';
                } else {
                    // Modalità modifica - POST a /edit (solo update, mai creazione)
                    const planningId = this.assignmentData && this.assignmentData.planning_id != null ? Number(this.assignmentData.planning_id) : null;
                    if (!this.assignmentData || planningId == null || !Number.isInteger(planningId)) {
                        throw new Error('Dati assegnazione non disponibili per la modifica');
                    }
                    
                    const editPayload = {
                        id: planningId,
                        project_id: this.editingAssignment.newProjectId,
                        subproject_id: this.editingAssignment.newSubprojectId || null,
                        hours: this.editingAssignment.newHours,
                        description: this.editingAssignment.newDescription || '',
                        user_id: this.editingAssignment.newUserId || (this.assignmentData.user ? this.assignmentData.user.id : null)
                    };
                    
                    // Aggiungi specific_date se è un task giornaliero
                    editPayload.specific_date = this.editingAssignment.specificDate || null;
                    
                    response = await axios.post('/admin/developers-planning/edit', editPayload);
                    successMessage = 'Assegnazione modificata con successo';
                }

                if (response.data.success) {
                    // Toast successo
                    if (window.toastr) {
                        toastr.success(successMessage);
                    }
                    
                    console.log('✅ ' + successMessage);
                    
                    // Emetti evento GLOBALE per refresh planning grid (usa window per comunicare tra Vue instances diverse)
                    window.dispatchEvent(new CustomEvent('planning-assignment-updated', {
                        detail: {
                            planningId: this.isCreationMode ? null : (this.assignmentData ? this.assignmentData.planning_id : null),
                            userId: this.assignmentData && this.assignmentData.user ? this.assignmentData.user.id : this.editingAssignment.newUserId,
                            newUserId: this.editingAssignment.newUserId,
                            isNew: this.isCreationMode
                        }
                    }));
                    
                    // Chiudi la sidebar
                    this.close(true);
                }
            } catch (error) {
                console.error('Errore salvataggio assegnazione:', error);
                const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Errore nel salvataggio dell\'assegnazione';
                this.showError(errorMessage);
            } finally {
                this.isSavingAssignment = false;
            }
        }
    },

    computed: {
        discussionEntries() {
            var comments = (this.comments || []).map(function(comment) {
                return {
                    kind: 'comment',
                    key: 'c-' + comment.id,
                    at: comment.created_at_iso || '',
                    comment: comment
                };
            });
            if (this.activityFilter !== 'all') {
                return comments;
            }
            var activities = (this.activities || []).map(function(activity) {
                return {
                    kind: 'activity',
                    key: 'a-' + activity.id,
                    at: activity.created_at_iso || '',
                    activity: activity
                };
            });
            return comments.concat(activities).sort(function(a, b) {
                return String(a.at).localeCompare(String(b.at));
            });
        },
        notificationRecipientLabel() {
            var count = this.notificationRecipientCount || 0;
            if (!count) {
                return '';
            }
            return count === 1
                ? '1 persona riceverà una notifica'
                : count + ' persone riceveranno una notifica';
        },
        canEditTaskStatus() {
            return this.canModify || this.canModifyTaskStatus;
        },

        taskStatusSelectOptions() {
            if (typeof window.__taskStatusOptions !== 'undefined' && window.__taskStatusOptions && window.__taskStatusOptions.length) {
                return window.__taskStatusOptions;
            }
            return [];
        },

        descriptionGalleryAttachments() {
            var inlineIds = {};
            var text = this.descriptionText || '';
            var re = /\[(?:img|video|file):(\d+)\]/g;
            var match;
            while ((match = re.exec(text)) !== null) {
                inlineIds[match[1]] = true;
            }
            var attrRe = /data-attachment-id="(\d+)"/g;
            while ((match = attrRe.exec(text)) !== null) {
                inlineIds[match[1]] = true;
            }
            return (this.attachments || []).filter(function(attachment) {
                return !inlineIds[String(attachment.id)];
            });
        },

        isCommentEmpty() {
            return this.isRichTextEmpty(this.newComment);
        },

        isEditCommentEmpty() {
            return this.isRichTextEmpty(this.editingCommentText);
        },

        filteredProjectUsers() {
            if (!this.assigneeSearchQuery) {
                return this.projectUsers;
            }
            
            const query = this.assigneeSearchQuery.toLowerCase();
            return this.projectUsers.filter(user => 
                user.full_name.toLowerCase().includes(query)
            );
        },

        difference() {
            if (!this.assignmentData) return 0;
            return this.assignmentData.actual_hours - this.assignmentData.planned_hours;
        },

        completionPercentage() {
            if (!this.assignmentData || !this.assignmentData.planned_hours) return 0;
            return Math.min(100, Math.round((this.assignmentData.actual_hours / this.assignmentData.planned_hours) * 100));
        },

        isCreationMode() {
            // Modalità creazione quando planning_id è null o non presente
            return !this.assignmentData || !this.assignmentData.planning_id;
        },

        isAssignmentValid() {
            const hasProject = this.editingAssignment.newProjectId || this.editingAssignment.newSubprojectId;
            const hasHours = this.editingAssignment.newHours && this.editingAssignment.newHours >= 0.5;
            const hasUser = this.isCreationMode ? this.editingAssignment.newUserId : true;
            
            return hasProject && hasHours && hasUser;
        },

        /**
         * Computed property per renderizzare la descrizione con HTML (tabelle, formattazione Markdown)
         * Usata nella modalità visualizzazione (non editing)
         */
        descriptionHtml() {
            if (!this.descriptionText && !this.formattedDescription) {
                return '';
            }

            if (this.descriptionFormat === 'html') {
                return this.formattedDescription || this.descriptionText || '';
            }
            
            // Usa formattedDescription dal backend (ha [img:ID] e [file:ID] già risolti)
            let html = this.formattedDescription || this.descriptionText || '';
            
            // Se contiene già tabelle HTML, non processare come Markdown
            if (/<table[\s\S]*<\/table>/i.test(html)) {
                return html;
            }
            
            // Proteggi contenuti che non devono essere processati
            const protectedItems = [];
            let protectedIndex = 0;
            const protect = (content) => {
                const placeholder = `PROTECTED${protectedIndex}CONTENT`;
                protectedItems.push({ placeholder, content });
                protectedIndex++;
                return placeholder;
            };
            
            // Proteggi blocchi HTML generati dal backend (file, immagini, missing)
            html = html.replace(/<div class="comment-(file|image|video)-container">[\s\S]*?<\/div>/gi, function(tag) {
                return protect(tag);
            });
            html = html.replace(/<span class="comment-(file|image|video)-missing">[^<]*<\/span>/gi, function(tag) {
                return protect(tag);
            });
            
            // Proteggi link <a>...</a> esistenti
            html = html.replace(/<a\s[^>]*>[\s\S]*?<\/a>/gi, function(tag) {
                return protect(tag);
            });
            
            // Auto-link URL solo nel testo libero (i link esistenti sono protetti)
            html = html.replace(
                /(?<!["\'>\/])((https?:\/\/|www\.)[^\s<>"']+)/gi,
                function(match) {
                    let url = match.replace(/[.,;:!?\)\]]+$/, '');
                    const href = url.startsWith('www.') ? 'https://' + url : url;
                    const safeUrl = url.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    const safeHref = href.replace(/&/g, '&amp;');
                    return protect('<a href="' + safeHref + '" target="_blank" rel="noopener noreferrer">' + safeUrl + '</a>');
                }
            );
            
            // Escape HTML per sicurezza (ma preserva i tag esistenti se presenti)
            if (!/<[a-z][\s\S]*>/i.test(html)) {
                html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            }
            
            // Parser Markdown leggero
            
            // Bold: **testo** o __testo__
            html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
            html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
            
            // Italic: *testo* o _testo_ (ma non dentro le parole)
            html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
            html = html.replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>');
            
            // Link Markdown: [testo](url)
            html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, function(match, text, url) {
                return protect('<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + text + '</a>');
            });
            
            // Liste puntate: righe che iniziano con - o *
            html = html.replace(/^[\-\*]\s+(.+)$/gm, '<li class="md-bullet">$1</li>');
            html = html.replace(/(<li class="md-bullet">.*<\/li>\n?)+/g, '<ul class="md-list">$&</ul>');
            
            // Liste numerate: righe che iniziano con 1. 2. ecc
            html = html.replace(/^\d+\.\s+(.+)$/gm, '<li class="md-number">$1</li>');
            html = html.replace(/(<li class="md-number">.*<\/li>\n?)+/g, '<ol class="md-list">$&</ol>');
            
            // Converti newline rimanenti in <br>
            html = html.replace(/\n/g, '<br>');
            
            // Pulisci <br> extra dentro le liste
            html = html.replace(/<\/li><br>/g, '</li>');
            html = html.replace(/<br><li/g, '<li');
            
            // Reinserisci contenuti protetti
            protectedItems.forEach(({ placeholder, content }) => {
                html = html.replace(placeholder, content);
            });
            
            return html;
        },

        /**
         * Rileva se il testo contiene formattazione Markdown
         */
        hasMarkdownFormatting() {
            if (!this.descriptionText) return false;
            const text = this.descriptionText;
            
            // Rileva: **bold**, *italic*, [link](url), - liste, 1. liste, URL
            return /\*\*.+?\*\*/.test(text) ||           // Bold
                   /__.+?__/.test(text) ||               // Bold alternativo
                   /(?<!\*)\*(?!\*).+?(?<!\*)\*(?!\*)/.test(text) || // Italic
                   /(?<!_)_(?!_).+?(?<!_)_(?!_)/.test(text) ||       // Italic alternativo
                   /\[.+?\]\(.+?\)/.test(text) ||        // Link Markdown
                   /^[\-\*]\s+.+$/m.test(text) ||        // Lista puntata
                   /^\d+\.\s+.+$/m.test(text) ||         // Lista numerata
                   /(https?:\/\/|www\.)[^\s]+/.test(text); // URL
        },

        /**
         * Determina se mostrare la descrizione in modalità view (con HTML) o edit (textarea)
         */
        showDescriptionView() {
            // Mostra la view HTML se:
            // 1. Non stiamo editando
            // 2. C'è del testo nella descrizione
            // Questo permette di visualizzare sempre il Markdown renderizzato
            return !this.isEditingDescription && this.descriptionText;
        },

    },

    watch: {
        isAddingDep(val) {
            if (val) {
                this.$nextTick(() => {
                    if (this.$refs.depSearchInput) {
                        this.$refs.depSearchInput.focus();
                    }
                });
            }
        },
        descriptionText() {
            if (!this.isEditingDescription || !this.canModify || this.isPlanningMode) {
                return;
            }
            this.debounceSaveDescription();
        },
        editingCommentText() {
            if (!this.editingCommentId) {
                return;
            }
            this.debounceSaveEditComment();
        },
        subprojectId(newVal, oldVal) {
            console.log('🔵 Watcher subprojectId attivato!', { newVal, oldVal, type: typeof newVal });
            
            // Se cambia task (da un task a un altro, non da null)
            if (oldVal && newVal && newVal !== oldVal) {
                console.log('🔵 Watcher: cambio task, resetto stato e ricarico');

                if (this.isOpen) {
                    console.log('🔵 Sidebar già aperta, ricarico dati nuovo task');
                    this.reloadCurrentTask(oldVal);
                    return;
                }
            }
            
            // Se newVal è valido (non null/undefined), apri la sidebar
            if (newVal && newVal !== oldVal) {
                console.log('🔵 Watcher: subprojectId cambiato, apro sidebar');
                this.open();
            } else if (!newVal && oldVal && this.isOpen) {
                // Se diventa null/undefined e la sidebar è aperta, chiudila
                console.log('🔵 Watcher: subprojectId azzerato, chiudo sidebar');
                this.close();
            } else {
                console.log('ℹ️ Watcher subprojectId: nessuna azione necessaria', { 
                    hasNewVal: !!newVal, 
                    hasOldVal: !!oldVal,
                    isOpen: this.isOpen
                });
            }
        },
        
        projectId(newVal, oldVal) {
            console.log('🔵 Watcher projectId attivato!', { 
                newVal, 
                oldVal, 
                subprojectId: this.subprojectId,
                type: typeof newVal 
            });
            
            // Se cambia projectId, ricarica gli utenti del progetto
            if (newVal && newVal !== oldVal) {
                console.log('🔵 Watcher: caricamento utenti per progetto', newVal);
                axios.get(`/projects/${newVal}/users`)
                    .then(response => {
                        var users = response.data.users || [];
                        if (users.length > 0) {
                            this.projectUsers = users;
                        }
                        console.log('✅ Utenti progetto caricati dal watcher:', this.projectUsers.length);
                    })
                    .catch(error => {
                        console.error('❌ Errore caricamento utenti progetto dal watcher:', error);
                    });
            }
            
            // Se cambia projectId E non c'è un subprojectId specifico, apri la sidebar
            // (per il caso: click su progetto senza task specifico ma con ore consuntivate)
            if (newVal && newVal !== oldVal && !this.subprojectId) {
                console.log('🔵 Watcher: projectId cambiato senza task specifico, apro sidebar per project');
                this.open();
            } else if (!newVal && oldVal && this.isOpen) {
                // Se diventa null e la sidebar è aperta, chiudila
                console.log('🔵 Watcher: projectId azzerato, chiudo sidebar');
                this.close();
            } else {
                console.log('ℹ️ Watcher projectId: nessuna azione necessaria', { 
                    hasNewVal: !!newVal, 
                    hasOldVal: !!oldVal,
                    hasSubprojectId: !!this.subprojectId,
                    isOpen: this.isOpen
                });
            }
        },
        
        weekStart(newVal, oldVal) {
            // Se cambia la settimana, resetta i timesheet per ricaricarli
            if (newVal !== oldVal && this.isOpen && this.activeTab === 'timesheet') {
                console.log('📅 Watcher weekStart: settimana cambiata, reset timesheet');
                this.timesheetEntries = [];
                this.loadTimesheetData();
            }
        },
        
        projectNameProp(newVal) {
            // Quando arriva il nome del progetto dalla prop, aggiornalo nei dati
            if (newVal) {
                console.log('📁 Watcher projectNameProp: nome progetto ricevuto:', newVal);
                this.taskData.project_name = newVal;
            }
        },
        
        async assignmentData(newVal) {
            // Se è modalità creazione (planning_id null), apri la sidebar
            if (newVal && !newVal.planning_id && this.isPlanningMode) {
                console.log('✨ Watcher assignmentData: modalità CREAZIONE, apro sidebar');
                
                // In modalità creazione, svuota i campi progetto e task
                this.clearProject();
                this.clearSubproject();
                
                // Reset TUTTI i campi per nuova pianificazione
                this.editingAssignment.newHours = null;
                this.editingAssignment.newDescription = '';
                
                // Nuova assegnazione: pre-compila specificDate con oggi se è nella settimana visualizzata,
                // altrimenti usa week_start (il giorno su cui l'utente ha cliccato)
                if (newVal.week_start) {
                    const today = new Date();
                    const todayStr = today.getFullYear() + '-' + 
                        String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                        String(today.getDate()).padStart(2, '0');
                    
                    const weekStart = newVal.week_start;
                    const weekEnd = newVal.week_end || newVal.week_start;
                    
                    // Se oggi è nel range della settimana visualizzata, usa oggi come default
                    if (todayStr >= weekStart && todayStr <= weekEnd) {
                        this.editingAssignment.specificDate = todayStr;
                        console.log('📅 Data specifica pre-compilata con oggi:', todayStr);
                    } else {
                        // Altrimenti usa week_start (il giorno su cui si è cliccato)
                        this.editingAssignment.specificDate = weekStart;
                        console.log('📅 Data specifica pre-compilata con week_start:', weekStart);
                    }
                } else {
                    this.editingAssignment.specificDate = null;
                }
                
                // Carica gli utenti disponibili (una volta sola)
                await this.loadAvailableUsers();
                
                // Pre-compila utente dalla riga su cui si è cliccato
                if (newVal.user && newVal.user.id) {
                    this.editingAssignment.newUserId = newVal.user.id;
                    this.editingAssignment.newUserName = newVal.user.full_name;
                    this.userSearchQuery = newVal.user.full_name;
                    console.log('👤 Utente pre-compilato per nuova assegnazione:', newVal.user.full_name);
                }
                
                this.open();
                
                // Inizializza datepicker per la creazione e snapshot per conferma chiusura
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.initializeTaskSidebarSpecificDatePicker();
                        this.$nextTick(() => {
                            this.capturePlanningCreationBaseline();
                        });
                    }, 150);
                });
            }
            
            // Pre-compila i campi di modifica assegnazione con i valori correnti
            if (newVal && this.isPlanningMode && newVal.planning_id) {
                this.planningCreationBaseline = null;
                console.log('📝 Watcher assignmentData: pre-compilo campi modifica assegnazione', newVal);
                
                // Carica gli utenti disponibili (una volta sola)
                await this.loadAvailableUsers();
                
                // Pre-compila progetto corrente
                if (newVal.project_id && newVal.project_name) {
                    this.editingAssignment.newProjectId = newVal.project_id;
                    this.editingAssignment.newProjectName = newVal.project_name;
                    this.projectSearchQuery = newVal.project_name;
                    
                    // Carica i task disponibili per questo progetto e ASPETTA il completamento
                    await this.loadAvailableSubprojects(newVal.project_id);
                    console.log('📋 Task caricati, ora pre-compilo il task corrente');
                }
                
                // Pre-compila task corrente (se presente) - DOPO che i subprojects sono caricati
                if (newVal.subproject_id && newVal.subproject_name) {
                    this.editingAssignment.newSubprojectId = newVal.subproject_id;
                    this.editingAssignment.newSubprojectName = newVal.subproject_name;
                    this.subprojectSearchQuery = newVal.subproject_name;
                
                console.log('📝 Task pre-compilato:', this.subprojectSearchQuery);
                }
                
                // Pre-compila utente corrente
                if (newVal.user && newVal.user.id) {
                    this.editingAssignment.newUserId = newVal.user.id;
                    this.editingAssignment.newUserName = newVal.user.full_name;
                    this.userSearchQuery = newVal.user.full_name;
                    console.log('👤 Utente pre-compilato:', newVal.user.full_name);
                }
                
                // Pre-compila ore pianificate correnti
                this.editingAssignment.newHours = newVal.planned_hours || null;
                
                // Pre-compila descrizione corrente
                this.editingAssignment.newDescription = newVal.description || '';
                
                // Pre-compila data specifica se presente
                this.editingAssignment.specificDate = newVal.specific_date || null;
                
                // Inizializza datepicker dopo il pre-compilamento
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.initializeTaskSidebarSpecificDatePicker();
                    }, 100);
                });
                
                console.log('✅ Campi pre-compilati:', this.editingAssignment);
                if (!this.isOpen) {
                    this.open();
                }
            }
        }
    },

    mounted() {
        console.log('🔵 TaskDetailSidebar mounted!', {
            subprojectId: this.subprojectId,
            projectId: this.projectId,
            canModify: this.canModify
        });
        
        // Carica lista utenti del progetto per autocomplete menzioni
        if (this.projectId) {
        axios.get(`/projects/${this.projectId}/users`)
            .then(response => {
                var users = response.data.users || [];
                if (users.length > 0) {
                    this.projectUsers = users;
                }
                console.log('✅ Utenti progetto caricati:', this.projectUsers.length);
            })
            .catch(error => {
                    console.error('❌ Errore caricamento utenti progetto:', error);
            });
        }
        
        // Scroll listener rimosso - causava problemi con touchpad
        // this.$nextTick(() => {
        //     const sidebarContent = this.$el.querySelector('.task-sidebar-content');
        //     if (sidebarContent) {
        //         sidebarContent.addEventListener('wheel', this.preventParentScroll, { passive: false });
        //     }
        // });
        
        // Listener per chiudere la sidebar quando si clicca fuori
        // Usa capture phase per intercettare prima degli altri handler
        this._documentClickHandler = (event) => {
            if (!this.isOpen) return;
            
            if (Date.now() - this._openedAt < 400) return;
            
            const target = event.target;
            
            // Verifica se il click è dentro la sidebar (controlla sia $el che .task-sidebar)
            const sidebar = this.$el ? this.$el.querySelector('.task-sidebar') : null;
            if (sidebar && sidebar.contains(target)) {
                return; // Click dentro la sidebar, non fare nulla
            }
            
            // Verifica se il click è su un elemento della sidebar Vue root
            if (this.$el && this.$el.contains(target)) {
                return;
            }
            
            // Verifica se il click è su un task della grid (per permettere il cambio task)
            // Solo se siamo nella pagina progetti, non in MyBrain
            const isTaskElement = target.closest('.main-task-row, .subtask-row, tr[data-id], [data-task-id], .open-task-detail-mybrain');
            const isTaskGrid = target.closest('.subprojects-grid');
            
            if (isTaskElement || isTaskGrid) {
                console.log('🔵 Click su task, non chiudo la sidebar');
                return; // Click su un task, il cambio sarà gestito dal click handler del task
            }

            if (target.closest('.project-allocation, .clickable-task-name, .open-task-detail-icon')) {
                return;
            }
            
            // SweetAlert2 non usa .modal Bootstrap: senza questo, in capture phase ogni click su
            // Annulla/Conferma richiama close() → nuovo Swal → loop infinito.
            if (document.body && document.body.classList.contains('swal2-shown')) {
                return;
            }
            if (target.closest('.swal2-container')) {
                return;
            }

            // Verifica se il click è su un modal o dropdown aperto (non chiudere in questi casi)
            const isModal = target.closest('.modal, .dropdown-menu, .select2-dropdown, .mention-suggestions, .ql-mention-list-container, .task-mention-menu, .task-slash-menu');
            if (isModal) {
                return; // Click su modal/dropdown, non chiudere la sidebar
            }
            
            // Click fuori dalla sidebar e non su elementi speciali, chiudi
            console.log('🔵 Click fuori dalla sidebar, chiudo');
            this.close();
        };
        
        // Aggiungi il listener con capture per intercettare prima degli altri
        document.addEventListener('click', this._documentClickHandler, true);
        
        this._documentPasteHandler = this.handleGlobalPaste.bind(this);
        document.addEventListener('paste', this._documentPasteHandler);
    },

    beforeDestroy() {
        // Rimuovi datepicker se presente
        if (this.$refs.taskSidebarSpecificDateInput) {
            const $input = $(this.$refs.taskSidebarSpecificDateInput);
            if ($input.data('daterangepicker')) {
                $input.data('daterangepicker').remove();
            }
        }
        
        // Scroll listener rimosso
        // const sidebarContent = this.$el.querySelector('.task-sidebar-content');
        // if (sidebarContent) {
        //     sidebarContent.removeEventListener('wheel', this.preventParentScroll);
        // }
        
        // Rimuovi event listener per click fuori
        if (this._documentClickHandler) {
            document.removeEventListener('click', this._documentClickHandler, true);
            this._documentClickHandler = null;
        }
        
        if (this._documentPasteHandler) {
            document.removeEventListener('paste', this._documentPasteHandler);
            this._documentPasteHandler = null;
        }

        this.clearRichTextSaveTimers();
        this.disconnectFromChannel();
    }
};
</script>

<style scoped>
.task-sidebar-title .editable-title,
.task-sidebar-title .task-title-readonly,
.task-sidebar .section-title {
    color: #e0e0e0 !important;
}

.section-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 6px;
    margin-top: 10px;
    color: #e0e0e0;
    display: flex;
    align-items: center;
    gap: 6px;
}

.section-title i {
    color: #667eea;
}

.section-divider {
    border: none;
    border-top: 1px solid #444;
    margin: 25px 0;
}

.overview-tab {
    display: flex;
    flex-direction: column;
}

.task-kpi-block {
    flex-shrink: 0; /* Non si restringe */
}

.description-section {
    flex-shrink: 0;
}

.chat-section {
    display: flex;
    flex-direction: column;
}

.chat-section .comments-list {
    /* Padding in basso per non coprire l'input fisso */
    padding-bottom: 100px;
}

/* Input commento fisso in basso (flex-item diretto di .task-sidebar) */
.comment-input-container-fixed {
    flex: 0 1 auto !important;
    box-sizing: border-box !important;
    max-height: 50% !important;
    min-height: 0 !important;
    overflow: hidden !important;
}

/* Stili figli separati per evitare merge del minificatore in produzione */
.comment-input-container-fixed >>> .mention-input-wrapper {
    flex: 1 1 auto;
    position: relative;
    min-width: 0;
}

.comment-input-container-fixed >>> .mention-textarea {
    background: #2a2a2a !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 10px 15px !important;
    color: #e0e0e0 !important;
    font-family: 'Sofia Pro', sans-serif !important;
    font-size: 14px !important;
    resize: none !important;
    width: 100% !important;
    min-height: 32px !important;
    max-height: 200px !important;
    box-sizing: border-box !important;
}

.comment-input-container-fixed >>> .mention-textarea::placeholder {
    color: rgba(255, 255, 255, 0.4) !important;
}

.comment-input-container-fixed >>> .mention-textarea:focus {
    outline: none !important;
    box-shadow: none !important;
}

.comment-input-container-fixed >>> .markdown-live-preview .preview-content {
    max-height: 80px;
}

/* Wysiwyg editor nel box commenti */
.comment-input-container-fixed >>> .wysiwyg-editor-wrapper {
    min-width: 0;
    display: flex;
    flex-direction: column;
}

/* Mobile responsive */
@media (max-width: 768px) {
    .task-sidebar {
        width: 100vw !important;
        max-width: 100vw !important;
        right: -100vw !important;
        transform: none !important;
        transition: right 300ms cubic-bezier(0.4, 0, 0.2, 1) !important;
        height: 100dvh !important;
        height: -webkit-fill-available !important;
        padding-bottom: env(safe-area-inset-bottom, 0px) !important;
    }
    .task-sidebar.is-open {
        right: 0 !important;
        transform: none !important;
    }
    .comment-input-container-fixed {
        padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px)) !important;
    }
}

.empty-state-small {
    text-align: center;
    padding: 20px;
    color: #888;
}

.empty-state-small i {
    font-size: 32px;
    margin-bottom: 10px;
    display: block;
}

.empty-state-small p {
    margin: 0;
    font-size: 14px;
}

/* Stili per menzioni e immagini inline sono in _task-sidebar.scss */

/* Stili tab Timesheet */
.timesheet-section {
    padding: 15px 0;
}

.week-indicator {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    font-size: 13px;
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.2);
}

.week-indicator i {
    font-size: 14px;
}

.timesheet-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.timesheet-item {
    background: #2a2a2a;
    border-radius: 8px;
    padding: 12px;
    border-left: 3px solid #667eea;
    transition: transform 0.2s ease;
}

.timesheet-item:hover {
    transform: translateX(2px);
    background: #323232;
}

.timesheet-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    gap: 12px;
}

.timesheet-date {
    font-size: 12px;
    color: #888;
    display: flex;
    align-items: center;
    gap: 6px;
}

.timesheet-date i {
    color: #667eea;
}

.timesheet-user {
    font-size: 11px;
    color: #999;
    display: flex;
    align-items: center;
    gap: 5px;
    font-weight: 500;
}

.timesheet-user i {
    color: #667eea;
    font-size: 10px;
}

.timesheet-user {
    font-size: 11px;
    color: #999;
    display: flex;
    align-items: center;
    gap: 4px;
}

.timesheet-user i {
    font-size: 10px;
}

.timesheet-task-name {
    font-size: 11px;
    color: #667eea;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
}

.timesheet-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
}

.timesheet-description {
    flex: 1;
    color: #e0e0e0;
    font-size: 14px;
    line-height: 1.4;
}

.timesheet-duration {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: #667eea;
    font-size: 14px;
    white-space: nowrap;
}

.timesheet-duration i {
    font-size: 12px;
}

.loading-indicator {
    text-align: center;
    padding: 40px 20px;
    color: #888;
}

.loading-indicator i {
    font-size: 24px;
    margin-bottom: 10px;
    display: block;
}

/* Task Info Row - Compatta */
.task-info-row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 10px 12px;
    background: #2d2d2d;
    border-radius: 6px;
    margin-bottom: 10px;
    flex-wrap: wrap;
}

/* Separatore verticale tra info e utenti */
.info-separator {
    width: 1px;
    height: 20px;
    background: rgba(255, 255, 255, 0.15);
    margin: 0 4px;
}

/* Utenti Assegnati Inline Compatto (dentro task-info-row) */
.assigned-users-inline-compact {
    display: flex;
    align-items: center;
    gap: 8px;
}

.info-item-inline {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #e0e0e0;
}

.info-item-inline i {
    color: #667eea;
    font-size: 12px;
}

.info-item-inline .info-value {
    font-weight: 500;
}

.info-item-inline .inline-date-input {
    font-size: 11px;
    padding: 2px 4px;
    width: 100px;
}

.inline-priority-select {
    font-size: 11px;
    font-weight: 600;
    padding: 4px 8px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    outline: none;
    position: relative;
    z-index: 10;
    -webkit-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3E%3Cpath fill='%23fff' d='M0 2l4 4 4-4z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 6px center;
    padding-right: 18px;
}

.inline-priority-select.priority-low {
    background-color: #28a745;
    color: white;
}

.inline-priority-select.priority-medium {
    background-color: #e67e22;
    color: white;
}

.inline-priority-select.priority-high {
    background-color: #dc3545;
    color: white;
}

.priority-text-low { color: #28a745; }
.priority-text-medium { color: #e67e22; }
.priority-text-high { color: #dc3545; }

/* Utenti Assegnati Inline (legacy - mantenuto per retrocompatibilità) */
.assigned-users-inline {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #2d2d2d;
    border-radius: 6px;
    margin-bottom: 10px;
}

/* Nascosto se vuoto, usato solo in altri contesti */

.assigned-label {
    color: #667eea;
    font-size: 12px;
}

.assigned-label.clickable {
    cursor: pointer;
}

.users-avatars-inline {
    display: flex;
    align-items: center;
    padding-left: 8px; /* Compensa il margin negativo del primo elemento */
}

.user-avatar-small {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #3a3a3a;
    font-size: 10px;
    color: #fff;
    cursor: help;
    transition: transform 0.2s, z-index 0s;
    border: 2px solid #2d2d2d; /* Bordo per separare visivamente quando sovrapposti */
    box-sizing: border-box;
}

.user-avatar-small:hover {
    transform: scale(1.15);
    z-index: 10;
}

.user-avatar-small img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.user-avatar-small .initials {
    font-weight: 600;
}

.user-avatar-small.add-btn {
    cursor: pointer;
    background: #667eea;
    color: white;
    margin-left: 4px; /* Il bottone + non si sovrappone */
}

.user-avatar-small.add-btn:hover {
    background: #5a6fd6;
}

.user-avatar-wrapper {
    position: relative;
    display: inline-block;
    margin-left: -8px; /* Sovrapposizione ~30% */
}

.user-avatar-wrapper:first-child {
    margin-left: 0;
}

.user-remove-btn {
    position: absolute;
    top: -4px;
    left: -4px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #dc3545;
    color: white;
    border: none;
    font-size: 10px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
    padding: 0;
    z-index: 5;
}

.user-avatar-wrapper:hover .user-remove-btn {
    opacity: 1;
}

/* Dipendenze */
.dependencies-section {
    padding: 8px 12px;
    flex-shrink: 0;
}
.dependencies-section .section-title {
    font-size: 12px;
    color: #aaa;
    margin: 0;
    font-weight: 600;
}
.dep-list {
    margin-bottom: 4px;
}
.dep-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    color: #ddd;
    transition: background 0.15s;
}
.dep-item:hover {
    background: rgba(255,255,255,0.05);
}
.dep-icon {
    font-size: 10px;
    flex-shrink: 0;
}
.dep-icon.pred { color: #f39c12; }
.dep-icon.succ { color: #3498db; }
.dep-code {
    font-weight: 600;
    color: #888;
    font-size: 11px;
    flex-shrink: 0;
}
.dep-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.dep-remove {
    background: none;
    border: none;
    color: #666;
    cursor: pointer;
    padding: 2px 4px;
    font-size: 10px;
    opacity: 0;
    transition: opacity 0.15s, color 0.15s;
}
.dep-item:hover .dep-remove {
    opacity: 1;
}
.dep-remove:hover {
    color: #e74c3c;
}
.dep-add {
    margin-top: 4px;
}
.dep-add-trigger {
    font-size: 11px;
    color: #667eea;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background 0.15s;
}
.dep-add-trigger:hover {
    background: rgba(102, 126, 234, 0.1);
}
.dep-add-trigger i {
    margin-right: 4px;
}
.dep-add-form {
    position: relative;
}
.dep-search-input {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 6px;
    background: rgba(255,255,255,0.06);
    color: #fff;
    font-size: 12px;
    outline: none;
}
.dep-search-input:focus {
    border-color: #667eea;
}
.dep-search-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 200px;
    overflow-y: auto;
    background: #2a2a3e;
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 0 0 6px 6px;
    z-index: 100;
}
.dep-search-item {
    padding: 6px 10px;
    font-size: 12px;
    color: #ddd;
    cursor: pointer;
    transition: background 0.1s;
}
.dep-search-item:hover {
    background: rgba(102, 126, 234, 0.2);
}
.dep-search-item .dep-code {
    margin-right: 6px;
}
.dep-empty {
    font-size: 11px;
    color: #666;
    padding: 4px 8px;
    font-style: italic;
}

/* Task Created Info */
.task-created-info {
    font-size: 11px;
    color: #888;
    padding: 4px 12px;
    margin-bottom: 8px;
}

.task-created-info i {
    margin-right: 4px;
    color: #667eea;
}

.task-created-info strong {
    color: #aaa;
}

/* Vecchi stili per compatibilità */
.info-value {
    color: #e0e0e0;
    font-weight: 500;
}

.section-label {
    font-size: 12px;
    color: #888;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.section-label i {
    color: #667eea;
}

.users-avatars-compact {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.user-avatar-compact {
    position: relative;
    cursor: help;
    transition: transform 0.2s ease;
    display: inline-flex !important;
    visibility: visible !important;
    z-index: 1;
}

.user-avatar-compact:hover {
    transform: scale(1.15);
    z-index: 10;
}

.user-avatar-img {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    min-height: 32px !important;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #2d2d2d;
    display: block !important;
    visibility: visible !important;
}

.user-avatar-initials {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    min-height: 32px !important;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex !important;
    visibility: visible !important;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    color: white;
    border: 2px solid #2d2d2d;
}

.users-avatars-compact {
    display: flex !important;
    visibility: visible !important;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

/* Blocco Assegnazione Settimana */
.assignment-week-block {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 16px;
    margin-bottom: 12px;
    border-radius: 8px;
    color: white;
}

.assignment-header {
    margin-bottom: 16px;
}

.assignment-user {
    display: flex !important;
    visibility: visible !important;
    align-items: center;
    gap: 12px;
}

.user-avatar-medium {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    min-height: 40px !important;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid white;
    display: block !important;
    visibility: visible !important;
}

.user-avatar-placeholder-medium {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    min-height: 40px !important;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid white;
    display: flex !important;
    visibility: visible !important;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: bold;
    color: white;
}

.user-info {
    display: flex;
    flex-direction: column;
}

.user-info strong {
    font-size: 15px;
}

.user-role {
    font-size: 12px;
    opacity: 0.8;
}

.assignment-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 12px;
}

.stat-item {
    background: rgba(255, 255, 255, 0.1);
    padding: 12px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.stat-item i {
    font-size: 20px;
}

.stat-content {
    display: flex;
    flex-direction: column;
}

.stat-label {
    font-size: 11px;
    opacity: 0.8;
}

.stat-value {
    font-size: 16px;
    font-weight: bold;
}

.stat-item.planned i {
    color: #ffd700;
}

.stat-item.actual i {
    color: #4CAF50;
}

.stat-item.difference.positive i {
    color: #4CAF50;
}

.stat-item.difference.negative i {
    color: #ff6b6b;
}

.progress-bar-container {
    height: 8px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
}

.progress-bar-fill {
    height: 100%;
    background: white;
    transition: width 0.3s ease;
}

.progress-percentage {
    text-align: center;
    font-size: 13px;
    font-weight: 600;
}

/* ========== INLINE EDITING STYLES ========== */

.editable-title {
    cursor: text;
    transition: background-color 0.2s ease;
    padding: 2px 4px;
    border-radius: 4px;
}

.editable-title:hover {
    background-color: rgba(255, 255, 255, 0.05);
}

.editable-title:focus {
    outline: none;
    background-color: rgba(255, 255, 255, 0.1);
}

.info-value.editable {
    cursor: text;
    padding: 2px 4px;
    border-radius: 3px;
    transition: background-color 0.2s ease;
}

.info-value.editable:hover {
    background-color: rgba(255, 255, 255, 0.05);
}

.info-value.editable:focus,
.info-value.editable[contenteditable]:focus {
    outline: none;
    background-color: rgba(255, 255, 255, 0.1);
}

.info-value.editable[data-placeholder]:empty::before {
    content: attr(data-placeholder);
    color: #666;
    font-style: italic;
}

.info-value.clickable {
    cursor: pointer;
    user-select: none;
}

.info-value.clickable:hover {
    background-color: rgba(255, 255, 255, 0.08);
}

.inline-date-input {
    background: transparent;
    border: none;
    color: #fff;
    font-size: inherit;
    font-family: inherit;
    padding: 2px 4px;
    cursor: pointer;
    border-radius: 3px;
    transition: background-color 0.2s ease;
    width: auto;
}

.inline-date-input:hover {
    background-color: rgba(255, 255, 255, 0.05);
}

.inline-date-input:focus {
    outline: none;
    background-color: rgba(255, 255, 255, 0.1);
}

/* Badge editabile */
.badge-editable {
    cursor: pointer;
    user-select: none;
    transition: transform 0.2s ease;
}

.badge-editable:hover {
    transform: translateY(-1px);
}

.badge-editable i {
    margin-left: 4px;
    font-size: 10px;
}

/* Dropdown inline */
.inline-dropdown {
    position: fixed;
    background: #2a2a2a;
    border: 1px solid #444;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    z-index: 9999;
    min-width: 150px;
    padding: 4px 0;
}

.dropdown-item {
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.dropdown-item:hover {
    background-color: rgba(255, 255, 255, 0.1);
}

/* Utenti assegnati editabili */
.section-label.clickable {
    cursor: pointer;
    user-select: none;
    transition: color 0.2s ease;
}

.section-label.clickable:hover {
    color: #667eea;
}

.section-label .edit-icon {
    margin-left: 8px;
    font-size: 11px;
    opacity: 0.7;
}

.user-avatar-compact {
    position: relative;
}

.remove-user-btn {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 16px;
    height: 16px;
    background: #e74c3c;
    border: none;
    border-radius: 50%;
    color: white;
    font-size: 10px;
    cursor: pointer;
    display: none;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;
}

.user-avatar-compact:hover .remove-user-btn {
    display: flex;
}

.user-avatar-compact.add-user {
    background: rgba(255, 255, 255, 0.1);
    border: 2px dashed rgba(255, 255, 255, 0.3);
    cursor: pointer;
    transition: all 0.2s ease;
}

.user-avatar-compact.add-user:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.5);
}

.user-avatar-compact.add-user i {
    color: rgba(255, 255, 255, 0.6);
}

/* Modal inline per assegnatari */
.inline-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    z-index: 9998;
    display: flex;
    align-items: center;
    justify-content: center;
}

.inline-modal {
    background: #2a2a2a;
    border-radius: 8px;
    width: 90%;
    max-width: 400px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.inline-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #444;
}

.inline-modal-header h5 {
    margin: 0;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
}

.inline-modal-body {
    padding: 16px 20px;
    overflow-y: auto;
    flex: 1;
}

.search-input {
    width: 100%;
    padding: 10px 12px;
    background: #1e1e1e;
    border: 1px solid #444;
    border-radius: 6px;
    color: #fff;
    font-size: 14px;
    margin-bottom: 12px;
}

.search-input:focus {
    outline: none;
    border-color: #667eea;
}

.user-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.user-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.user-item:hover {
    background-color: rgba(255, 255, 255, 0.05);
}

.user-item.selected {
    background-color: rgba(102, 126, 234, 0.2);
}

.user-avatar-small {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    min-height: 32px !important;
    border-radius: 50%;
    overflow: hidden;
    display: flex !important;
    visibility: visible !important;
    flex-shrink: 0;
}

.user-avatar-small img {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    object-fit: cover;
    display: block !important;
    visibility: visible !important;
}

.avatar-initials-small {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    min-height: 32px !important;
    border-radius: 50%;
    background: #667eea;
    display: flex !important;
    visibility: visible !important;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: white;
}

.user-name {
    flex: 1;
    color: #ddd;
}

.user-item i.fa-check {
    color: #667eea;
    font-size: 14px;
}

/* Placeholder per scadenza vuota */
.editable-placeholder {
    cursor: pointer;
    color: #888;
    font-style: italic;
    padding: 2px 4px;
    border-radius: 3px;
    transition: all 0.2s ease;
}

.editable-placeholder:hover {
    background-color: rgba(255, 255, 255, 0.05);
    color: #aaa;
}

/* Breadcrumb Progetto e Task Padre */
.task-breadcrumb {
    font-size: 11px;
    color: #888;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.breadcrumb-item {
    color: #aaa;
}

.breadcrumb-separator {
    color: #666;
    font-weight: normal;
}

/* Sezione Modifica Assegnazione */
.edit-assignment-section {
    margin-top: 20px;
    padding: 15px;
    background: rgba(255, 152, 0, 0.1);
    border-left: 3px solid #ff9800;
    border-radius: 4px;
}

.edit-assignment-section h6 {
    color: #ff9800;
    margin-bottom: 15px;
    margin-top: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
}

.edit-assignment-section .alert {
    background: rgba(255, 152, 0, 0.15);
    border: 1px solid #ff9800;
    color: #ffa726;
    padding: 10px;
    border-radius: 4px;
    font-size: 13px;
}

.autocomplete-container {
    position: relative;
    margin-bottom: 15px;
}

.autocomplete-container label {
    display: block;
    margin-bottom: 5px;
    font-size: 12px;
    color: #aaa;
    font-weight: 500;
}

.autocomplete-input {
    width: 100%;
    padding: 8px 30px 8px 10px;
    background: #2a2a2a;
    border: 1px solid #444;
    color: #e0e0e0;
    border-radius: 4px;
    font-size: 14px;
    transition: border-color 0.2s ease;
}

.autocomplete-input:focus {
    border-color: #667eea;
    outline: none;
}

.autocomplete-input:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.autocomplete-clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: #888;
    font-size: 14px;
    transition: color 0.2s ease;
}

.autocomplete-clear:hover {
    color: #e0e0e0;
}

.autocomplete-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #2a2a2a;
    border: 1px solid #667eea;
    max-height: 250px;
    overflow-y: auto;
    z-index: 1000;
    border-radius: 4px;
    margin-top: 2px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.autocomplete-item {
    padding: 4px 8px;
    cursor: pointer;
    color: #e0e0e0;
    font-size: 13px;
    transition: background-color 0.15s ease;
    line-height: 1.3;
}

.autocomplete-item:hover {
    background: #667eea;
}

.autocomplete-item-selected {
    background: #764ba2 !important;
    color: #fff !important;
}

.autocomplete-no-results {
    padding: 12px;
    text-align: center;
    color: #888;
    font-size: 12px;
}

.edit-assignment-section .form-group label {
    display: block;
    margin-bottom: 5px;
    font-size: 12px;
    color: #aaa;
    font-weight: 500;
}

.edit-assignment-section .form-control {
    background: #2a2a2a;
    border: 1px solid #444;
    color: #e0e0e0;
    padding: 8px 10px;
    border-radius: 4px;
    font-size: 14px;
}

.edit-assignment-section .form-control:focus {
    border-color: #667eea;
    outline: none;
}

/* Blocco KPI Task nella Panoramica */
.task-kpi-block {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 16px;
    margin-bottom: 20px;
    border-radius: 8px;
    color: white;
}

.kpi-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 12px;
}

.kpi-item {
    background: rgba(255, 255, 255, 0.1);
    padding: 12px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.kpi-item i {
    font-size: 20px;
}

.kpi-content {
    display: flex;
    flex-direction: column;
}

.kpi-label {
    font-size: 11px;
    opacity: 0.8;
}

.kpi-value {
    font-size: 16px;
    font-weight: bold;
}

.kpi-item.estimated i {
    color: #87CEEB;
}

.kpi-item.planned i {
    color: #ffd700;
}

.kpi-item.actual i {
    color: #4CAF50;
}

.kpi-item.difference.positive i {
    color: #4CAF50;
}

.kpi-item.difference.negative i {
    color: #ff6b6b;
}

.kpi-progress-container {
    height: 8px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
}

.kpi-progress-bar-fill {
    height: 100%;
    background: white;
    transition: width 0.3s ease;
}

.kpi-progress-percentage {
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    opacity: 0.9;
}

/* Stili per paste immagini nella descrizione */
.description-input-wrapper {
    position: relative;
}

.description-input-wrapper.is-dragover .description-textarea {
    border-color: #667eea !important;
    background: rgba(102, 126, 234, 0.1) !important;
}

.description-input-wrapper .drag-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(102, 126, 234, 0.9);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 14px;
    font-weight: 600;
    z-index: 20;
    pointer-events: none;
}

.description-input-wrapper .drag-overlay i {
    font-size: 24px;
    margin-bottom: 8px;
}

.description-input-wrapper .image-upload-indicator {
    position: absolute;
    bottom: 12px;
    right: 12px;
    background: rgba(102, 126, 234, 0.9);
    color: white;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Preview immagini incollate */
.pasted-images-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    padding: 8px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 6px;
}

.pasted-image-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 6px;
    padding: 6px 10px;
}

.pasted-image-thumb {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.pasted-image-name {
    font-size: 12px;
    color: #e0e0e0;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Description view mode (HTML renderizzato con tabelle) */
.description-view {
    background: #2a2a2a;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 10px 15px;
    color: #e0e0e0;
    font-family: 'Sofia Pro', sans-serif;
    font-size: 14px;
    min-height: 50px;
    cursor: text;
    transition: border-color 200ms ease;
    line-height: 1.5;
}

.description-view:hover {
    border-color: rgba(102, 126, 234, 0.4);
}

/* Tabelle HTML incollate da Excel/Sheets */
.description-view >>> table,
.description-section >>> table,
.comment-text >>> table {
    width: 100%;
    border-collapse: collapse;
    margin: 10px 0;
    font-size: 13px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 6px;
    overflow: hidden;
}

.description-view >>> table th,
.description-view >>> table td,
.description-section >>> table th,
.description-section >>> table td,
.comment-text >>> table th,
.comment-text >>> table td {
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 10px 14px;
    text-align: left;
    vertical-align: top;
}

.description-view >>> table thead th,
.description-section >>> table thead th,
.comment-text >>> table thead th {
    background: rgba(102, 126, 234, 0.25);
    font-weight: 600;
    color: #fff;
}

.description-view >>> table td,
.description-section >>> table td,
.comment-text >>> table td {
    color: rgba(224, 224, 224, 0.9);
}

.description-view >>> table tbody tr:nth-child(even) td,
.description-section >>> table tbody tr:nth-child(even) td,
.comment-text >>> table tbody tr:nth-child(even) td {
    background: rgba(255, 255, 255, 0.02);
}

.description-view >>> table tbody tr:hover td,
.description-section >>> table tbody tr:hover td,
.comment-text >>> table tbody tr:hover td {
    background: rgba(102, 126, 234, 0.1);
}

/* Preview tabella incollata nella descrizione */
.pasted-table-preview {
    margin-top: 8px;
    background: rgba(102, 126, 234, 0.1);
    border: 1px solid rgba(102, 126, 234, 0.3);
    border-radius: 8px;
    overflow: hidden;
}

/* Preview tabella durante editing commento */
.editing-table-preview {
    margin-top: 10px;
    margin-bottom: 10px;
}

.pasted-table-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: rgba(102, 126, 234, 0.15);
    color: #667eea;
    font-size: 13px;
    font-weight: 500;
}

.pasted-table-header i {
    font-size: 14px;
}

.pasted-table-remove {
    margin-left: auto;
    background: none;
    border: none;
    color: #888;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.2s;
}

.pasted-table-remove:hover {
    background: rgba(220, 53, 69, 0.2);
    color: #dc3545;
}

.pasted-table-content {
    padding: 0;
    max-height: 300px;
    overflow: auto;
    border-radius: 6px;
}

/* Deep selector per penetrare nel contenuto v-html */
.pasted-table-content >>> table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    background: #1a1a2e;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.pasted-table-content >>> th,
.pasted-table-content >>> td {
    border: 1px solid rgba(255, 255, 255, 0.25) !important;
    padding: 10px 14px;
    text-align: left;
    color: #e0e0e0;
}

.pasted-table-content >>> thead th {
    background: #2d2d5a !important;
    font-weight: 600;
    color: #fff;
    position: sticky;
    top: 0;
    border-bottom: 2px solid rgba(102, 126, 234, 0.5) !important;
}

.pasted-table-content >>> tbody tr:nth-child(odd) td {
    background: rgba(30, 30, 50, 0.8);
}

.pasted-table-content >>> tbody tr:nth-child(even) td {
    background: rgba(40, 40, 70, 0.8);
}

.pasted-table-content >>> tbody tr:hover td {
    background: rgba(102, 126, 234, 0.2);
}
</style>

